import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import AbaFotos from '../components/produtos/AbaFotos';
import AbaCurso from '../components/produtos/AbaCurso';
import { navegarComEnter } from '../utils/enterNavegacao';
import useBuscaProdutosF3, { FILTROS_F3_VAZIOS } from '../hooks/useBuscaProdutosF3';
import ModalBuscaProdutos from '../components/pdv/ModalBuscaProdutos';
import s from './CadastroProdutos.module.css';

/* ────────────────────────────────────────────────────────
   Constantes
   ──────────────────────────────────────────────────────── */
const ABAS = [
  { id: 'geral',        label: 'Geral' },
  { id: 'fiscal',       label: 'Fiscal' },
  { id: 'precos',       label: 'Tabela de Preço' },
  { id: 'fornecedores', label: 'Fornecedores' },
  { id: 'ficha',        label: 'Composição/Ficha Técnica' },
  { id: 'plataformas',  label: 'Plataformas de Venda' },
  { id: 'kit',          label: 'Kit' },
  { id: 'lotes',        label: 'Lotes/Validades' },
  { id: 'balanca',      label: 'Balança' },
  { id: 'foto',         label: 'Foto' },
  { id: 'curso',        label: 'Curso em Vídeo' },
  { id: 'outros',       label: 'Outros' },
];

const TIPOS = ['mercadoria', 'serviço', 'matéria-prima', 'embalagem', 'uso e consumo'];

const TIPOS_CB = [
  { valor: 'EAN-13',         label: 'EAN-13 (padrão varejo)' },
  { valor: 'EAN-8',          label: 'EAN-8 (produto pequeno)' },
  { valor: 'DUN-14',         label: 'DUN-14 (caixa/fardo)' },
  { valor: 'QR Code',        label: 'QR Code' },
  { valor: 'Código Interno', label: 'Código Interno' },
  { valor: 'Fabricante',     label: 'Cód. Fabricante' },
  { valor: 'Code 128',       label: 'Code 128' },
  { valor: 'Code 39',        label: 'Code 39' },
];

const normalizeTipo = t => ({
  EAN13: 'EAN-13', EAN8: 'EAN-8', DUN14: 'DUN-14',
  QRCode: 'QR Code', Code128: 'Code 128', Code39: 'Code 39', Balança: 'EAN-13',
}[t] ?? t);

const ORIGENS_FISCAL = [
  { v: '0', l: '0 — Nacional, exceto indicados nos códigos 3, 4, 5 e 8' },
  { v: '1', l: '1 — Estrangeira — importação direta' },
  { v: '2', l: '2 — Estrangeira — adquirida no mercado interno' },
  { v: '3', l: '3 — Nacional — mercado interno com mais de 40% de conteúdo estrangeiro' },
  { v: '4', l: '4 — Nacional — produção em conformidade com Ajuste Sinief 70/89' },
  { v: '5', l: '5 — Nacional — com menos de 40% de conteúdo estrangeiro' },
  { v: '6', l: '6 — Estrangeira — importação direta sem similar nacional' },
  { v: '7', l: '7 — Estrangeira — adquirida no mercado interno sem similar nacional' },
  { v: '8', l: '8 — Nacional — mercado interno com mais de 70% de conteúdo estrangeiro' },
];

const TIPOS_FISCAL = [
  { v: '00', l: '00 — Mercadoria para Revenda' },
  { v: '01', l: '01 — Matéria-Prima' },
  { v: '02', l: '02 — Embalagem' },
  { v: '03', l: '03 — Produto em Processo' },
  { v: '04', l: '04 — Produto Acabado' },
  { v: '05', l: '05 — Subproduto' },
  { v: '06', l: '06 — Produto Intermediário' },
  { v: '07', l: '07 — Material de Uso e Consumo' },
  { v: '08', l: '08 — Ativo Imobilizado' },
  { v: '09', l: '09 — Serviços' },
  { v: '10', l: '10 — Outros Insumos' },
  { v: '99', l: '99 — Outras' },
];

const CSOSN_OPTS = [
  { v: '',    l: '— não informado —' },
  { v: '101', l: '101 — Tributada pelo Simples Nacional com permissão de crédito' },
  { v: '102', l: '102 — Tributada pelo Simples Nacional sem permissão de crédito' },
  { v: '103', l: '103 — Isenção do ICMS para faixa de receita bruta' },
  { v: '201', l: '201 — Tributada pelo Simples Nacional com permissão de crédito e com cobrança do ICMS por ST' },
  { v: '202', l: '202 — Tributada pelo Simples Nacional sem permissão de crédito e com cobrança do ICMS por ST' },
  { v: '203', l: '203 — Isenção do ICMS para faixa de receita bruta e com cobrança do ICMS por ST' },
  { v: '300', l: '300 — Imune' },
  { v: '400', l: '400 — Não tributada pelo Simples Nacional' },
  { v: '500', l: '500 — ICMS cobrado anteriormente por ST ou por antecipação' },
  { v: '900', l: '900 — Outros' },
];

const SUB_ABAS_FISCAL = [
  { id: 'geral',   label: 'Geral' },
  { id: 'entrada', label: 'Entrada' },
  { id: 'saida',   label: 'Saída' },
  { id: 'fcp',     label: 'FCP por UF' },
];

const ESTADOS = [
  { uf: 'AC', nome: 'Acre' },           { uf: 'AL', nome: 'Alagoas' },
  { uf: 'AP', nome: 'Amapá' },          { uf: 'AM', nome: 'Amazonas' },
  { uf: 'BA', nome: 'Bahia' },          { uf: 'CE', nome: 'Ceará' },
  { uf: 'DF', nome: 'Distrito Federal' },{ uf: 'ES', nome: 'Espírito Santo' },
  { uf: 'GO', nome: 'Goiás' },          { uf: 'MA', nome: 'Maranhão' },
  { uf: 'MT', nome: 'Mato Grosso' },    { uf: 'MS', nome: 'Mato Grosso do Sul' },
  { uf: 'MG', nome: 'Minas Gerais' },   { uf: 'PA', nome: 'Pará' },
  { uf: 'PB', nome: 'Paraíba' },        { uf: 'PR', nome: 'Paraná' },
  { uf: 'PE', nome: 'Pernambuco' },     { uf: 'PI', nome: 'Piauí' },
  { uf: 'RJ', nome: 'Rio de Janeiro' }, { uf: 'RN', nome: 'Rio Grande do Norte' },
  { uf: 'RS', nome: 'Rio Grande do Sul' },{ uf: 'RO', nome: 'Rondônia' },
  { uf: 'RR', nome: 'Roraima' },        { uf: 'SC', nome: 'Santa Catarina' },
  { uf: 'SP', nome: 'São Paulo' },      { uf: 'SE', nome: 'Sergipe' },
  { uf: 'TO', nome: 'Tocantins' },
];

// CST (regime normal) é código de 2 dígitos ("00".."90") — não 3. Achado
// real (2026-09-16): esta lista tinha os 11 primeiros valores errados,
// zero-preenchidos pra 3 dígitos ("020" em vez de "20"), sem bater com o
// que o banco guarda nem com o que o backend espera no <CST> do XML
// (services/sefaz/icms.js#montarIcmsNormal faz `padStart(2,'0')` — só
// GARANTE 2 dígitos MÍNIMOS, não corta um valor de 3; um "020" salvo por
// aqui cairia fora de todos os `case` do switch e quebraria a emissão
// desse produto). CSOSN (Simples Nacional, 101-900) é 3 dígitos mesmo,
// esses continuam certos. Nenhum cliente tinha valor salvo com o bug
// (conferido nos 12 antes de corrigir) — só a exibição/seleção manual
// estava exposta ao risco, nunca chegou a corromper dado real.
const CSTS_ICMS = [
  { v: '00', l: '00 — Tributada integralmente' },
  { v: '10', l: '10 — Tributada e com cobrança ICMS-ST' },
  { v: '20', l: '20 — Com redução de base de cálculo' },
  { v: '30', l: '30 — Isenta/não tributada e com cobrança ICMS-ST' },
  { v: '40', l: '40 — Isenta' },
  { v: '41', l: '41 — Não tributada' },
  { v: '50', l: '50 — Com suspensão' },
  { v: '51', l: '51 — Com diferimento' },
  { v: '60', l: '60 — ICMS cobrado anteriormente por substituição tributária' },
  { v: '70', l: '70 — Com redução de B.C. e cobrança ICMS-ST' },
  { v: '90', l: '90 — Outras' },
  { v: '101', l: '101 — Tributada pelo SN com permissão de crédito' },
  { v: '102', l: '102 — Tributada pelo SN sem permissão de crédito' },
  { v: '103', l: '103 — Isenção ICMS no SN para faixa de receita bruta' },
  { v: '201', l: '201 — Trib. pelo SN c/ crédito e c/ ICMS-ST' },
  { v: '202', l: '202 — Trib. pelo SN s/ crédito e c/ ICMS-ST' },
  { v: '203', l: '203 — Isenção no SN e com ICMS-ST' },
  { v: '300', l: '300 — Imune' },
  { v: '400', l: '400 — Não tributada pelo Simples Nacional' },
  { v: '500', l: '500 — ICMS cobrado anteriormente por ST ou antecipação' },
  { v: '900', l: '900 — Outros (SN)' },
];

// CST de IBS/CBS — Reforma Tributária (NT 2025.002, obrigatoriedade
// 01/01/2027). Tabela própria, diferente da CST de ICMS acima — a NT ainda
// está em revisão pela SEFAZ, então antes de emitir documento fiscal de
// verdade com esses códigos, confira a versão vigente no portal da NF-e.
const CSTS_IBSCBS = [
  { v: '000', l: '000 — Tributação integral' },
  { v: '010', l: '010 — Tributação com alíquota reduzida' },
  { v: '011', l: '011 — Tributação com alíquota reduzida e diferimento' },
  { v: '020', l: '020 — Tributação com redução de base de cálculo' },
  { v: '200', l: '200 — Isenção' },
  { v: '210', l: '210 — Imunidade' },
  { v: '220', l: '220 — Suspensão' },
  { v: '221', l: '221 — Suspensão — Diferimento' },
  { v: '400', l: '400 — Não incidência' },
  { v: '410', l: '410 — Não incidência — Zona Franca de Manaus / Áreas de Livre Comércio' },
  { v: '500', l: '500 — Diferimento' },
  { v: '510', l: '510 — Diferimento — ZFM / ALC' },
  { v: '550', l: '550 — Suspensão do IBS/CBS' },
  { v: '620', l: '620 — Tributação monofásica' },
  { v: '900', l: '900 — Outros' },
];

const MODS_BC_ICMS = [
  { v: '0', l: '0 — Margem Valor Agregado (%)' },
  { v: '1', l: '1 — Pauta (Valor)' },
  { v: '2', l: '2 — Preço Tabelado Máximo (valor)' },
  { v: '3', l: '3 — Valor da Operação' },
];

const MODS_BC_ICMS_ST = [
  { v: '0', l: '0 — Preço Tabelado ou Máximo Sugerido' },
  { v: '1', l: '1 — Lista Negativa (valor)' },
  { v: '2', l: '2 — Lista Positiva (valor)' },
  { v: '3', l: '3 — Lista Neutra (valor)' },
  { v: '4', l: '4 — Margem Valor Agregado (%)' },
  { v: '5', l: '5 — Pauta (valor)' },
  { v: '6', l: '6 — Valor da Operação' },
];

const CSTS_PIS_COFINS_ENT = [
  { v: '01', l: '01 — Op. Tributável com Alíquota Básica' },
  { v: '02', l: '02 — Op. Tributável com Alíquota Diferenciada' },
  { v: '03', l: '03 — Op. Tributável por Unidade de Medida' },
  { v: '04', l: '04 — Op. Monofásica — Revenda a Alíquota Zero' },
  { v: '05', l: '05 — Op. por Substituição Tributária' },
  { v: '06', l: '06 — Op. Tributável a Alíquota Zero' },
  { v: '07', l: '07 — Op. Isenta da Contribuição' },
  { v: '08', l: '08 — Op. sem Incidência da Contribuição' },
  { v: '09', l: '09 — Op. com Suspensão da Contribuição' },
  { v: '49', l: '49 — Outras Operações de Entrada' },
  { v: '50', l: '50 — Crédito Vinculado a Receita Tributada no Mercado Interno' },
  { v: '51', l: '51 — Crédito Vinculado a Receita Não Tributada no Mercado Interno' },
  { v: '52', l: '52 — Crédito Vinculado a Receita de Exportação' },
  { v: '53', l: '53 — Crédito Vinculado a Receita Tributada e Não Tributada' },
  { v: '54', l: '54 — Crédito Vinculado a Receita Tributada e Exportação' },
  { v: '55', l: '55 — Crédito Vinculado a Receita Não Tributada e Exportação' },
  { v: '56', l: '56 — Crédito Vinculado a Todas as Receitas' },
  { v: '60', l: '60 — Crédito Presumido — Alíquota Básica' },
  { v: '61', l: '61 — Crédito Presumido — Alíquota Diferenciada' },
  { v: '62', l: '62 — Crédito Presumido — Alíquota por Unidade' },
  { v: '63', l: '63 — Crédito Presumido — Vinculado a Receita Tributada' },
  { v: '64', l: '64 — Crédito Presumido — Vinculado a Receita Não Tributada' },
  { v: '65', l: '65 — Crédito Presumido — Vinculado a Exportação' },
  { v: '66', l: '66 — Crédito Presumido — Vinculado a Receitas Mistas' },
  { v: '67', l: '67 — Crédito Presumido — Vinculado a Todas as Receitas' },
  { v: '70', l: '70 — Crédito de Estoque — Vinculado a Receita Tributada' },
  { v: '71', l: '71 — Crédito de Estoque — Vinculado a Receita Não Tributada' },
  { v: '72', l: '72 — Crédito de Estoque — Vinculado a Exportação' },
  { v: '73', l: '73 — Crédito de Estoque — Vinculado a Receitas Mistas' },
  { v: '74', l: '74 — Crédito de Estoque — Vinculado a Todas as Receitas' },
  { v: '75', l: '75 — Crédito Decorrente de Operação de Exportação' },
  { v: '98', l: '98 — Outras Operações de Entrada com Crédito' },
  { v: '99', l: '99 — Outras Operações de Entrada' },
];

const CSTS_PIS_COFINS_SAI = [
  { v: '01', l: '01 — Op. Tributável com Alíquota Básica' },
  { v: '02', l: '02 — Op. Tributável com Alíquota Diferenciada' },
  { v: '03', l: '03 — Op. Tributável por Unidade de Medida' },
  { v: '04', l: '04 — Op. Monofásica — Revenda a Alíquota Zero' },
  { v: '05', l: '05 — Op. por Substituição Tributária' },
  { v: '06', l: '06 — Op. Tributável a Alíquota Zero' },
  { v: '07', l: '07 — Op. Isenta da Contribuição' },
  { v: '08', l: '08 — Op. sem Incidência da Contribuição' },
  { v: '09', l: '09 — Op. com Suspensão da Contribuição' },
  { v: '49', l: '49 — Outras Operações de Saída' },
  { v: '99', l: '99 — Outras Operações' },
];

const FORM_VAZIO = {
  id: null,
  codigo_interno: '',
  nome: '',
  descricao: '',
  descricao_resumida: '',
  aplicacao: '',
  unidade: 'UN',
  tipo: 'mercadoria',
  servico_lc116_codigo: '',
  servico_ctribnac: '',
  servico_cnbs: '',
  duracao_min: '',
  inativo: false,
  marcador: false,
  composto: false,
  montar_automatico_venda: false,
  usar_custo_composicao: false,
  fornecedor_id: null, fornecedor_nome: '',
  marca_id: null,      marca_nome: '',
  grupo_id: null,      grupo_nome: '',
  subgrupo_id: null,   subgrupo_nome: '',
  preco_custo: '0.00',
  outros_custos_pct: '0.00',
  margem_pct: '0.00',
  preco_venda: '0.00',
  desconto_maximo_pct: '0.00',
  comissao_pct: '0.00',
  peso_kg: '0.000',
  fator_conversao: '1.0000',
  unidade_medida: '',
  estoque_minimo: '0',
  pesado_no_caixa: false,
  possui_acrescimo: false,
  producao_propria: false,
  produto_balanca: false,
  recebido_em_consignacao: false,
  permite_alterar_qtd_preco: false,
  item_cozinha: false,
  catalogo_online: false,
  // Fiscal principal
  csosn: '',
  // Fiscal detalhado
  ncm: '', ex_ncm: '', cest: '',
  origem_fiscal: '0', tipo_fiscal: '00',
  ean_fiscal: '', ean_trib: '', cod_beneficio: '',
  cod_anp: '', descricao_anp: '',
  glp_perc: '', glgnn_perc: '', glgni_perc: '',
  val_partida: '', aliq_adrem: '',
  ind_importador: false, cod_uf_combustivel: '', orig_uf_perc: '', biodiesel_perc: '',
  unidade_tributavel_diferente: false, fator_conversao_trib: '1.0000', unidade_tributavel: '',
  fiscal_entrada: {
    icms_habilitado: true, icms_cst: '',
    icms_mod_bc: '3', icms_red_bc_perc: '0.00', icms_aliq_perc: '0.00',
    icms_st_mod_bc: '4', icms_st_red_bc_perc: '0.00', icms_st_mva_perc: '0.00', icms_st_aliq_perc: '0.00',
    icms_st_ret_bc: '0.00', icms_st_ret_valor: '0.00',
    pis_habilitado: false, pis_cst: '', pis_aliq_perc: '',
    cofins_habilitado: false, cofins_cst: '', cofins_aliq_perc: '',
  },
  fiscal_saida: {
    icms_habilitado: true, icms_cst: '',
    icms_mod_bc: '3', icms_red_bc_perc: '0.00', icms_aliq_perc: '0.00',
    icms_aliq_efet_perc: '0.00', icms_dif_perc: '0.00',
    icms_st_mod_bc: '4', icms_st_red_bc_perc: '0.00', icms_st_mva_perc: '0.00', icms_st_aliq_perc: '0.00',
    icms_st_ret_bc: '0.00', icms_st_ret_valor: '0.00',
    pis_habilitado: false, pis_cst: '', pis_aliq_perc: '', pis_nat_rec: '',
    cofins_habilitado: false, cofins_cst: '', cofins_aliq_perc: '', cofins_nat_rec: '',
    // IBS/CBS — Reforma Tributária (NT 2025.002)
    ibscbs_cst: '', ibscbs_cclasstrib: '',
    ibs_aliq_perc: '', ibs_uf_destino: '', ibs_red_bc_perc: '', ibs_dif_perc: '',
    ibs_cred_pres_codigo: '', ibs_cred_pres_perc: '',
    cbs_aliq_perc: '', cbs_red_bc_perc: '', cbs_dif_perc: '',
    cbs_cred_pres_codigo: '', cbs_cred_pres_perc: '',
    is_incide: false, is_aliq_perc: '',
  },
  fcp_uf: [],
  codigos_barras: [],
  estoque_setores: [],
};

// Normaliza contra a lista canônica (case-insensitive) — cadastros antigos
// ou editados fora da tela (direto no banco) podem ter 'SERVIÇO' em vez do
// 'serviço' minúsculo que o <select> oferece. Sem isso, o <select value=...>
// não bate com nenhuma <option> e o navegador mostra a 1ª opção
// (mercadoria) mesmo o estado continuando com o valor velho — se o usuário
// não mexer no campo, ele salva de volta o valor errado (loop).
function normalizarTipo(tipo) {
  const achado = TIPOS.find(t => t.toLowerCase() === String(tipo || '').trim().toLowerCase());
  return achado || 'mercadoria';
}

function produtoParaForm(p) {
  return {
    id: p.id,
    codigo_interno: p.codigo_interno ?? '',
    nome: p.nome ?? '',
    descricao: p.descricao ?? '',
    descricao_resumida: p.descricao_resumida ?? '',
    aplicacao: p.aplicacao ?? '',
    unidade: p.unidade ?? 'UN',
    tipo: normalizarTipo(p.tipo),
    servico_lc116_codigo: p.servico_lc116_codigo ?? '',
    servico_ctribnac: p.servico_ctribnac ?? '',
    servico_cnbs: p.servico_cnbs ?? '',
    duracao_min: p.duracao_min ?? '',
    inativo: p.inativo ?? false,
    marcador: p.marcador ?? false,
    composto: p.composto ?? false,
    montar_automatico_venda: p.montar_automatico_venda ?? false,
    usar_custo_composicao: p.usar_custo_composicao ?? false,
    fornecedor_id: p.fornecedor_id ?? null,
    fornecedor_nome: p.fornecedor_nome ?? '',
    marca_id: p.marca_id ?? null,
    marca_nome: p.marca_nome ?? '',
    grupo_id: p.grupo_id ?? null,
    grupo_nome: p.grupo_nome ?? '',
    subgrupo_id: p.subgrupo_id ?? null,
    subgrupo_nome: p.subgrupo_nome ?? '',
    preco_custo: String(p.preco_custo ?? '0.00'),
    outros_custos_pct: String(p.outros_custos_pct ?? '0.00'),
    margem_pct: String(p.margem_pct ?? '0.00'),
    preco_venda: String(p.preco_venda ?? '0.00'),
    desconto_maximo_pct: String(p.desconto_maximo_pct ?? '0.00'),
    comissao_pct: String(p.comissao_pct ?? '0.00'),
    peso_kg: String(p.peso_kg ?? '0.000'),
    fator_conversao: String(p.fator_conversao ?? '1.0000'),
    unidade_medida: p.unidade_medida ?? '',
    estoque_minimo: String(p.estoque_minimo ?? '0'),
    pesado_no_caixa: p.pesado_no_caixa ?? false,
    possui_acrescimo: p.possui_acrescimo ?? false,
    producao_propria: p.producao_propria ?? false,
    produto_balanca: p.produto_balanca ?? false,
    recebido_em_consignacao: p.recebido_em_consignacao ?? false,
    permite_alterar_qtd_preco: p.permite_alterar_qtd_preco ?? false,
    item_cozinha: p.item_cozinha ?? false,
    catalogo_online: p.catalogo_online ?? false,
    // Fiscal
    csosn: p.csosn ?? '',
    ncm: p.ncm ?? '',
    ex_ncm: p.ex_ncm ?? '',
    cest: p.cest ?? '',
    origem_fiscal: String(p.origem_fiscal ?? '0'),
    tipo_fiscal: p.tipo_fiscal ?? '00',
    ean_fiscal: p.ean_fiscal ?? '',
    ean_trib: p.ean_trib ?? '',
    cod_beneficio: p.cod_beneficio ?? '',
    cod_anp: p.cod_anp ?? '',
    descricao_anp: p.descricao_anp ?? '',
    glp_perc: p.glp_perc != null ? String(p.glp_perc) : '',
    glgnn_perc: p.glgnn_perc != null ? String(p.glgnn_perc) : '',
    glgni_perc: p.glgni_perc != null ? String(p.glgni_perc) : '',
    val_partida: p.val_partida != null ? String(p.val_partida) : '',
    aliq_adrem: p.aliq_adrem != null ? String(p.aliq_adrem) : '',
    ind_importador: p.ind_importador ?? false,
    cod_uf_combustivel: p.cod_uf_combustivel ?? '',
    orig_uf_perc: p.orig_uf_perc != null ? String(p.orig_uf_perc) : '',
    biodiesel_perc: p.biodiesel_perc != null ? String(p.biodiesel_perc) : '',
    unidade_tributavel_diferente: p.unidade_tributavel_diferente ?? false,
    fator_conversao_trib: String(p.fator_conversao_trib ?? '1.0000'),
    unidade_tributavel: p.unidade_tributavel ?? '',
    fiscal_entrada: (() => {
      const fe = p.fiscal_entrada;
      const sv = (v, def = '') => (v != null ? String(v) : def);
      return {
        icms_habilitado: fe?.icms_habilitado ?? true, icms_cst: fe?.icms_cst ?? '',
        icms_mod_bc: sv(fe?.icms_mod_bc, '3'), icms_red_bc_perc: sv(fe?.icms_red_bc_perc, '0.00'), icms_aliq_perc: sv(fe?.icms_aliq_perc, '0.00'),
        icms_st_mod_bc: sv(fe?.icms_st_mod_bc, '4'), icms_st_red_bc_perc: sv(fe?.icms_st_red_bc_perc, '0.00'),
        icms_st_mva_perc: sv(fe?.icms_st_mva_perc, '0.00'), icms_st_aliq_perc: sv(fe?.icms_st_aliq_perc, '0.00'),
        icms_st_ret_bc: sv(fe?.icms_st_ret_bc, '0.00'), icms_st_ret_valor: sv(fe?.icms_st_ret_valor, '0.00'),
        pis_habilitado: fe?.pis_habilitado ?? false, pis_cst: fe?.pis_cst ?? '', pis_aliq_perc: sv(fe?.pis_aliq_perc),
        cofins_habilitado: fe?.cofins_habilitado ?? false, cofins_cst: fe?.cofins_cst ?? '', cofins_aliq_perc: sv(fe?.cofins_aliq_perc),
      };
    })(),
    fiscal_saida: (() => {
      const fs = p.fiscal_saida;
      const sv = (v, def = '') => (v != null ? String(v) : def);
      return {
        icms_habilitado: fs?.icms_habilitado ?? true, icms_cst: fs?.icms_cst ?? '',
        icms_mod_bc: sv(fs?.icms_mod_bc, '3'), icms_red_bc_perc: sv(fs?.icms_red_bc_perc, '0.00'), icms_aliq_perc: sv(fs?.icms_aliq_perc, '0.00'),
        icms_aliq_efet_perc: sv(fs?.icms_aliq_efet_perc, '0.00'), icms_dif_perc: sv(fs?.icms_dif_perc, '0.00'),
        icms_st_mod_bc: sv(fs?.icms_st_mod_bc, '4'), icms_st_red_bc_perc: sv(fs?.icms_st_red_bc_perc, '0.00'),
        icms_st_mva_perc: sv(fs?.icms_st_mva_perc, '0.00'), icms_st_aliq_perc: sv(fs?.icms_st_aliq_perc, '0.00'),
        icms_st_ret_bc: sv(fs?.icms_st_ret_bc, '0.00'), icms_st_ret_valor: sv(fs?.icms_st_ret_valor, '0.00'),
        pis_habilitado: fs?.pis_habilitado ?? false, pis_cst: fs?.pis_cst ?? '', pis_aliq_perc: sv(fs?.pis_aliq_perc), pis_nat_rec: fs?.pis_nat_rec ?? '',
        cofins_habilitado: fs?.cofins_habilitado ?? false, cofins_cst: fs?.cofins_cst ?? '', cofins_aliq_perc: sv(fs?.cofins_aliq_perc), cofins_nat_rec: fs?.cofins_nat_rec ?? '',
        // IBS/CBS — Reforma Tributária (NT 2025.002). Antes destas 2 linhas
        // não existirem, esses campos eram descartados aqui mesmo quando já
        // vinham preenchidos do banco (ex.: setados pela Alteração em
        // Massa) — a tela simplesmente não carregava, então parecia que o
        // valor tinha sumido.
        ibscbs_cst: fs?.ibscbs_cst ?? '', ibscbs_cclasstrib: fs?.ibscbs_cclasstrib ?? '',
        ibs_aliq_perc: sv(fs?.ibs_aliq_perc), ibs_uf_destino: fs?.ibs_uf_destino ?? '',
        ibs_red_bc_perc: sv(fs?.ibs_red_bc_perc), ibs_dif_perc: sv(fs?.ibs_dif_perc),
        ibs_cred_pres_codigo: fs?.ibs_cred_pres_codigo ?? '', ibs_cred_pres_perc: sv(fs?.ibs_cred_pres_perc),
        cbs_aliq_perc: sv(fs?.cbs_aliq_perc), cbs_red_bc_perc: sv(fs?.cbs_red_bc_perc), cbs_dif_perc: sv(fs?.cbs_dif_perc),
        cbs_cred_pres_codigo: fs?.cbs_cred_pres_codigo ?? '', cbs_cred_pres_perc: sv(fs?.cbs_cred_pres_perc),
        is_incide: fs?.is_incide ?? false, is_aliq_perc: sv(fs?.is_aliq_perc),
      };
    })(),
    fcp_uf: (() => {
      const m = Object.fromEntries((p.fcp_uf || []).map(f => [f.uf, String(f.fcp_perc ?? '0.00')]));
      return ESTADOS.map(e => ({ uf: e.uf, fcp_perc: m[e.uf] ?? '0.00' }));
    })(),
    codigos_barras: p.codigos_barras ?? [],
    estoque_setores: comQtdOriginal(p.estoque_setores ?? []),
  };
}

// Quantidade que veio do banco ao abrir o produto: no salvar o backend aplica
// só a diferença digitada, sem apagar reposição/venda feita com a tela aberta.
const comQtdOriginal = lista => lista.map(e => ({ ...e, quantidade_original: e.quantidade }));

function calcPrecoVenda(custo, outrosPct, margemPct) {
  const c = parseFloat(custo) || 0;
  const o = parseFloat(outrosPct) || 0;
  const m = parseFloat(margemPct) || 0;
  const custoTotal = c * (1 + o / 100);
  return (custoTotal / (1 - m / 100)).toFixed(2);
}

/* ────────────────────────────────────────────────────────
   Sub-componente: Modal de busca / cadastro auxiliar
   ──────────────────────────────────────────────────────── */
function ModalBusca({ tipo, titulo, endpoint, campos, onSelect, onClose }) {
  const [busca, setBusca] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [mostraNovo, setMostraNovo] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [nomeEditado, setNomeEditado] = useState('');
  const [erroAcao, setErroAcao] = useState('');
  const qc = useQueryClient();

  // Editar/excluir só faz sentido pra grupos e subgrupos por enquanto —
  // fornecedores/marcas continuam só busca+cadastro rápido, sem gestão aqui.
  const permiteGerenciar = tipo === 'grupos' || tipo === 'subgrupos';
  const campoNome = campos[0].key;

  const { data = [], isLoading } = useQuery({
    queryKey: [endpoint, busca],
    queryFn: () => api.get(`/${endpoint}`, { params: { busca } }).then(r => r.data),
  });

  const criar = useMutation({
    mutationFn: dados => api.post(`/${endpoint}`, dados),
    onSuccess: d => { qc.invalidateQueries([endpoint]); onSelect(d.data); },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, nome }) => api.put(`/${endpoint}/${id}`, { [campoNome]: nome }),
    onSuccess: () => { qc.invalidateQueries([endpoint]); setEditandoId(null); },
    onError: err => setErroAcao(err.response?.data?.erro || 'Erro ao salvar.'),
  });

  const excluir = useMutation({
    mutationFn: id => api.delete(`/${endpoint}/${id}`),
    onSuccess: (_d, id) => {
      qc.invalidateQueries([endpoint]);
      setErroAcao('');
      if (selecionado?.id === id) setSelecionado(null);
    },
    onError: err => setErroAcao(err.response?.data?.erro || 'Erro ao excluir.'),
  });

  const iniciarEdicao = (e, row) => {
    e.stopPropagation();
    setErroAcao('');
    setEditandoId(row.id);
    setNomeEditado(row[campoNome] || '');
  };

  const confirmarEdicao = (e, id) => {
    e.stopPropagation();
    if (!nomeEditado.trim()) return;
    atualizar.mutate({ id, nome: nomeEditado.trim() });
  };

  const handleExcluir = (e, row) => {
    e.stopPropagation();
    setErroAcao('');
    if (!window.confirm(`Excluir "${row[campoNome]}"?`)) return;
    excluir.mutate(row.id);
  };

  return (
    <div className={s.overlay} data-enter-scope="off" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal}>
        <div className={s.modalTitle}>Buscar / Cadastrar — {titulo}</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input className={s.inputErp} style={{ flex: 1 }} placeholder="Buscar..." value={busca}
              onChange={e => setBusca(e.target.value)} autoFocus />
            <button className={s.btnErp} onClick={() => setMostraNovo(v => !v)}>
              {mostraNovo ? '▲ Ocultar' : '+ Novo'}
            </button>
          </div>

          {mostraNovo && (
            <div className={s.modalForm}>
              <div className={s.modalFormTitle}>Novo {titulo}</div>
              {campos.map(c => (
                <div key={c.key} className={s.campo}>
                  <span className={s.campoLabel}>{c.label}</span>
                  <input className={`${s.inputErp} ${s.inputErp} full`}
                    style={{ flex: 1 }} value={novoNome}
                    onChange={e => setNovoNome(e.target.value)} />
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                <button className={s.btnErp}
                  onClick={() => criar.mutate({ [campos[0].key]: novoNome })}
                  disabled={!novoNome.trim() || criar.isPending}>
                  Salvar
                </button>
              </div>
            </div>
          )}

          {erroAcao && <div className={s.cbErroDup} style={{ margin: '4px 0' }}>✕ {erroAcao}</div>}

          <div style={{ flex: 1, overflow: 'auto' }}>
            {isLoading ? <p style={{ padding: 8, color: '#888' }}>Carregando...</p> : (
              <table className={s.modalTabela}>
                <thead>
                  <tr>
                    <th>Cód</th>
                    <th>Nome</th>
                    {permiteGerenciar && <th style={{ width: 76 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {data.map(row => (
                    <tr key={row.id}
                      className={selecionado?.id === row.id ? s.selecionado : ''}
                      onClick={() => setSelecionado(row)}
                      onDoubleClick={() => editandoId !== row.id && onSelect(row)}>
                      <td style={{ width: 50 }}>{row.id}</td>
                      <td>
                        {editandoId === row.id ? (
                          <input className={s.inputErp} autoFocus style={{ width: '100%' }}
                            value={nomeEditado}
                            onClick={e => e.stopPropagation()}
                            onChange={e => setNomeEditado(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') confirmarEdicao(e, row.id);
                              if (e.key === 'Escape') { e.stopPropagation(); setEditandoId(null); }
                            }} />
                        ) : row[campoNome]}
                      </td>
                      {permiteGerenciar && (
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                          {editandoId === row.id ? (<>
                            <button className={s.btnLookup} title="Confirmar"
                              disabled={atualizar.isPending}
                              onClick={e => confirmarEdicao(e, row.id)}>✓</button>
                            <button className={s.btnLookup} title="Cancelar"
                              onClick={e => { e.stopPropagation(); setEditandoId(null); }}>✕</button>
                          </>) : (<>
                            <button className={s.btnLookup} title="Editar"
                              onClick={e => iniciarEdicao(e, row)}>✎</button>
                            <button className={s.btnLookup} title="Excluir (só se não houver produto vinculado)"
                              disabled={excluir.isPending}
                              onClick={e => handleExcluir(e, row)}>🗑</button>
                          </>)}
                        </td>
                      )}
                    </tr>
                  ))}
                  {!data.length && <tr><td colSpan={permiteGerenciar ? 3 : 2} style={{ color: '#999', padding: 8 }}>Nenhum registro encontrado</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} disabled={!selecionado}
            onClick={() => selecionado && onSelect(selecionado)}>
            Selecionar
          </button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-componente: Últimas Compras
   ──────────────────────────────────────────────────────── */
function ModalUltimasCompras({ produtoId, nomeProduto, onClose }) {
  const navigate = useNavigate();
  const { data = [], isLoading } = useQuery({
    queryKey: ['compras', produtoId],
    queryFn: () => api.get(`/produtos/${produtoId}/compras`).then(r => r.data),
    enabled: !!produtoId,
  });

  const fmtBRL = v => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtData = d => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

  // Abre a Entrada de NF de origem dessa compra (a tela do módulo de Compras)
  const abrirEntrada = (entradaNfId) => {
    onClose();
    navigate('/compras/entradas-nf', { state: { abrirEntradaId: entradaNfId } });
  };

  return (
    <div className={s.overlay} data-enter-scope="off" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ minWidth: 620 }}>
        <div className={s.modalTitle}>Últimas Compras — {nomeProduto}</div>
        <div className={s.modalBody}>
          {isLoading ? <p>Carregando...</p> : (
            <table className={s.modalTabela}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Fornecedor</th>
                  <th>NF</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Qtde</th>
                  <th style={{ textAlign: 'right' }}>Custo Unit.</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.map(c => (
                  <tr key={c.id}
                    style={{ cursor: 'pointer' }}
                    title="Clique para abrir esta entrada de NF"
                    onClick={() => abrirEntrada(c.entrada_nf_id)}>
                    <td>{fmtData(c.data_emissao || c.data_entrada)}</td>
                    <td>{c.fornecedor || '—'}</td>
                    <td>{c.numero_nf || '—'}</td>
                    <td>{c.status === 'confirmada' ? 'Confirmada' : 'Pendente'}</td>
                    <td style={{ textAlign: 'right' }}>{Number(c.quantidade).toFixed(3)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtBRL(c.preco_custo)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtBRL(c.valor_total)}</td>
                  </tr>
                ))}
                {!data.length && (
                  <tr><td colSpan={7} style={{ color: '#999', padding: 10, textAlign: 'center' }}>Nenhuma compra registrada</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-componente: Modal de Código de Barras
   ──────────────────────────────────────────────────────── */
function ModalCodigo({ inicial, produtoId, onSalvar, onClose }) {
  const [codigo,    setCodigo]    = useState(inicial?.codigo    || '');
  const [tipo,      setTipo]      = useState(normalizeTipo(inicial?.tipo || 'EAN-13'));
  const [descricao, setDescricao] = useState(inicial?.descricao || '');
  const [principal, setPrincipal] = useState(inicial?.principal || false);
  // Quantas unidades o código representa: 1 = a unidade; caixa com 24 = 24
  // (bipar a caixa no PDV lança 24; a Entrada de NF reconhece pela caixa).
  const [quantidade, setQuantidade] = useState(String(Number(inicial?.quantidade || 1)).replace('.', ','));
  const [aviso,     setAviso]     = useState('');   // '' | 'verificando' | 'ok' | 'erro:...'

  const verificarCodigo = async val => {
    const cod = val.trim();
    if (!cod) { setAviso(''); return; }
    // Não reverifica se é o mesmo código sendo editado
    if (inicial?.codigo === cod) { setAviso(''); return; }
    setAviso('verificando');
    try {
      const { data } = await api.get('/produtos/verificar-codigo', {
        params: { codigo: cod, produto_id: produtoId || undefined },
      });
      setAviso(data.disponivel
        ? 'ok'
        : `erro:Código já usado: ${data.produto?.nome || 'outro produto'} (cod. ${data.produto?.codigo_interno})`);
    } catch { setAviso(''); }
  };

  const temErro    = aviso.startsWith('erro:');
  const verificando = aviso === 'verificando';
  const qtdNum = parseFloat(String(quantidade).replace(',', '.'));
  const qtdValida = Number.isFinite(qtdNum) && qtdNum > 0;
  const podeSalvar  = !!codigo.trim() && !temErro && !verificando && qtdValida;

  const handleSalvar = () => {
    if (!podeSalvar) return;
    onSalvar({ ...(inicial || {}), codigo: codigo.trim(), tipo, descricao: descricao.trim(), principal, quantidade: qtdNum });
  };

  return (
    <div className={s.overlay} data-enter-scope="off">
      <div className={s.modal} style={{ minWidth: 420 }}>
        <div className={s.modalTitle}>{inicial ? 'Editar' : 'Adicionar'} Código de Barras</div>
        <div className={s.modalBody}>
          <div className={s.campo}>
            <span className={s.campoLabel}>Código</span>
            <input className={s.inputErp} style={{ flex: 1, fontFamily: "'Courier New', monospace" }}
              value={codigo} autoFocus
              onChange={e => { setCodigo(e.target.value); setAviso(''); }}
              onBlur={e => verificarCodigo(e.target.value)} />
          </div>
          {aviso === 'verificando' && <div className={s.cbOk}>Verificando disponibilidade...</div>}
          {aviso === 'ok'          && <div className={s.cbOk}>✓ Código disponível</div>}
          {temErro                 && <div className={s.cbErroDup}>✕ {aviso.slice(5)}</div>}
          <div className={s.campo}>
            <span className={s.campoLabel}>Tipo</span>
            <select className={s.selectErp} style={{ flex: 1 }}
              value={tipo} onChange={e => setTipo(e.target.value)}>
              {TIPOS_CB.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
          </div>
          <div className={s.campo}>
            <span className={s.campoLabel}>Descrição</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={descricao} placeholder="Ex: Unidade, Caixa com 12, Cód. Fornecedor"
              onChange={e => setDescricao(e.target.value)} />
          </div>
          <div className={s.campo}>
            <span className={s.campoLabel}>Qtde. no código</span>
            <input className={s.inputErp} style={{ width: 90 }}
              value={quantidade} onChange={e => setQuantidade(e.target.value)} />
            <span style={{ fontSize: 11, color: '#666', marginLeft: 8 }}>
              {qtdValida && qtdNum > 1
                ? `Código de caixa: bipar no PDV lança ${String(qtdNum).replace('.', ',')} unidades`
                : '1 = código da unidade · caixa com 24 = 24'}
            </span>
          </div>
          <div className={s.checkRow} style={{ marginTop: 4 }}>
            <input type="checkbox" id="cbPrincipal" checked={principal}
              onChange={e => setPrincipal(e.target.checked)} />
            <label htmlFor="cbPrincipal">Principal — código usado no PDV e entrada de NF-e</label>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
            disabled={!podeSalvar}
            onClick={handleSalvar}>
            Salvar
          </button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-componente: Modal de Locais de Estoque (CRUD)
   ──────────────────────────────────────────────────────── */
function ModalLocaisEstoque({ onClose }) {
  const qc = useQueryClient();
  const [selecionado, setSelecionado] = useState(null);
  const [modoForm, setModoForm] = useState('novo');
  const [editForm, setEditForm] = useState({ id: null, sigla: '', nome: '', loja_numero: 1, ordem: 0 });

  const { data: locais = [], isLoading } = useQuery({
    queryKey: ['setores-estoque', 'todos'],
    queryFn: () => api.get('/setores-estoque', { params: { todas: 'true' } }).then(r => r.data),
  });

  const criar = useMutation({
    mutationFn: d => api.post('/setores-estoque', d),
    onSuccess: () => { qc.invalidateQueries(['setores-estoque']); resetForm(); },
    onError: err => alert(err.response?.data?.erro || 'Erro ao salvar'),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, d }) => api.put(`/setores-estoque/${id}`, d),
    onSuccess: () => { qc.invalidateQueries(['setores-estoque']); resetForm(); },
    onError: err => alert(err.response?.data?.erro || 'Erro ao salvar'),
  });

  const toggle = useMutation({
    mutationFn: id => api.patch(`/setores-estoque/${id}/toggle`),
    onSuccess: () => qc.invalidateQueries(['setores-estoque']),
  });

  const resetForm = () => {
    setModoForm('novo');
    setEditForm({ id: null, sigla: '', nome: '', loja_numero: 1, ordem: 0 });
  };

  const handleEditar = loc => {
    setModoForm('editar');
    setEditForm({ id: loc.id, sigla: loc.sigla, nome: loc.nome, loja_numero: loc.loja_numero || 1, ordem: loc.ordem || 0 });
  };

  const handleSalvar = () => {
    if (!editForm.sigla.trim() || !editForm.nome.trim()) return alert('Preencha o Código e o Nome do Local.');
    const d = { sigla: editForm.sigla, nome: editForm.nome, loja_numero: editForm.loja_numero, ordem: editForm.ordem };
    if (modoForm === 'novo') criar.mutate(d);
    else atualizar.mutate({ id: editForm.id, d: { ...d, ativo: true } });
  };

  return (
    <div className={s.overlay} data-enter-scope="off" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ minWidth: 620 }}>
        <div className={s.modalTitle}>Cadastro de Locais de Estoque</div>
        <div className={s.modalBody}>
          <div className={s.locaisGrid}>
            {/* Lista */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ fontSize: 12, color: '#555', fontWeight: 'bold' }}>
                {locais.length} local(is) cadastrado(s)
              </div>
              <div className={s.locaisLista}>
                {isLoading && <div style={{ padding: 10, color: '#888' }}>Carregando...</div>}
                {locais.map(loc => (
                  <div key={loc.id}
                    className={[s.localRow, selecionado?.id === loc.id ? s.localSel : '', !loc.ativo ? s.localInativo : ''].join(' ')}
                    onClick={() => setSelecionado(loc)}>
                    <span className={s.localLj}>LJ{loc.loja_numero || 1}</span>
                    <span className={s.localCod}>{loc.sigla}</span>
                    <span className={s.localNome}>{loc.nome}</span>
                    {!loc.ativo && <small style={{ fontSize: 11 }}>(inativo)</small>}
                    <div className={s.localAcoes}>
                      <button className={s.btnIcone} title="Editar"
                        onClick={e => { e.stopPropagation(); handleEditar(loc); }}>✎</button>
                      <button className={s.btnIcone}
                        title={loc.ativo ? 'Inativar' : 'Ativar'}
                        style={{ color: loc.ativo ? '#c00' : '#060' }}
                        onClick={e => { e.stopPropagation(); toggle.mutate(loc.id); }}>
                        {loc.ativo ? '✕' : '✓'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Formulário */}
            <div className={s.locaisForm}>
              <div className={s.locaisFormTitle}>{modoForm === 'novo' ? 'Novo Local' : 'Editar Local'}</div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 60 }}>Código</span>
                <input className={s.inputErp} style={{ width: 70 }} maxLength={10}
                  value={editForm.sigla} placeholder="LOC"
                  onChange={e => setEditForm(f => ({ ...f, sigla: e.target.value.toUpperCase() }))} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 60 }}>Nome</span>
                <input className={s.inputErp} style={{ flex: 1 }}
                  value={editForm.nome} placeholder="DEPÓSITO"
                  onChange={e => setEditForm(f => ({ ...f, nome: e.target.value }))} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 60 }}>LJ (nº)</span>
                <input className={s.inputErp} style={{ width: 50 }} type="number" min="1"
                  value={editForm.loja_numero}
                  onChange={e => setEditForm(f => ({ ...f, loja_numero: parseInt(e.target.value) || 1 }))} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 60 }}>Ordem</span>
                <input className={s.inputErp} style={{ width: 50 }} type="number" min="0"
                  value={editForm.ordem}
                  onChange={e => setEditForm(f => ({ ...f, ordem: parseInt(e.target.value) || 0 }))} />
              </div>
              <div style={{ display: 'flex', gap: 5, marginTop: 4 }}>
                <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                  onClick={handleSalvar} disabled={criar.isPending || atualizar.isPending}>
                  {modoForm === 'novo' ? 'Incluir' : 'Salvar'}
                </button>
                {modoForm === 'editar' && <button className={s.btnErp} onClick={resetForm}>Cancelar</button>}
              </div>
            </div>
          </div>
        </div>
        <div className={s.modalFooter}>
          <div style={{ flex: 1, fontSize: 11, color: '#666' }}>
            Novos locais aparecem automaticamente em todos os produtos
          </div>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-componente: Modal de Gerenciamento de Unidades (CRUD)
   ──────────────────────────────────────────────────────── */
function ModalUnidades({ onSelect, onClose }) {
  const qc = useQueryClient();
  const [selecionado, setSelecionado] = useState(null);
  const [editForm, setEditForm] = useState({ id: null, codigo: '', descricao: '', fraciona: false });
  const [modoForm, setModoForm] = useState('novo');

  const { data: unids = [], isLoading } = useQuery({
    queryKey: ['unidades', 'todas'],
    queryFn: () => api.get('/unidades', { params: { todas: 'true' } }).then(r => r.data),
  });

  const criar = useMutation({
    mutationFn: d => api.post('/unidades', d),
    onSuccess: () => { qc.invalidateQueries(['unidades']); setEditForm({ id: null, codigo: '', descricao: '', fraciona: false }); },
    onError: err => alert(err.response?.data?.erro || 'Erro ao salvar'),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, d }) => api.put(`/unidades/${id}`, d),
    onSuccess: () => { qc.invalidateQueries(['unidades']); setModoForm('novo'); setEditForm({ id: null, codigo: '', descricao: '', fraciona: false }); },
    onError: err => alert(err.response?.data?.erro || 'Erro ao salvar'),
  });

  const toggle = useMutation({
    mutationFn: id => api.patch(`/unidades/${id}/toggle`),
    onSuccess: () => qc.invalidateQueries(['unidades']),
  });

  const handleEditar = u => { setModoForm('editar'); setEditForm({ id: u.id, codigo: u.codigo, descricao: u.descricao, fraciona: !!u.fraciona }); };
  const handleCancelarForm = () => { setModoForm('novo'); setEditForm({ id: null, codigo: '', descricao: '', fraciona: false }); };

  const handleSalvarForm = () => {
    if (!editForm.codigo.trim() || !editForm.descricao.trim()) return alert('Preencha código e descrição.');
    if (modoForm === 'novo') criar.mutate({ codigo: editForm.codigo, descricao: editForm.descricao, fraciona: editForm.fraciona });
    else atualizar.mutate({ id: editForm.id, d: { codigo: editForm.codigo, descricao: editForm.descricao, ativo: true, fraciona: editForm.fraciona } });
  };

  return (
    <div className={s.overlay} data-enter-scope="off" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ minWidth: 600 }}>
        <div className={s.modalTitle}>Gerenciar Unidades de Medida</div>
        <div className={s.modalBody}>
          <div className={s.unidadesGrid}>
            {/* Lista de unidades */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ fontSize: 12, color: '#555', fontWeight: 'bold' }}>
                {unids.length} unidade(s) cadastrada(s) — duplo clique para selecionar
              </div>
              <div className={s.unidadesLista}>
                {isLoading && <div style={{ padding: 10, color: '#888' }}>Carregando...</div>}
                {unids.map(u => (
                  <div key={u.id}
                    className={[s.unidadeRow, selecionado?.id === u.id ? s.unidadeSel : '', !u.ativo ? s.unidadeInativa : ''].join(' ')}
                    onClick={() => setSelecionado(u)}
                    onDoubleClick={() => onSelect(u.codigo)}>
                    <span className={s.unidadeCod}>{u.codigo}</span>
                    <span className={s.unidadeDesc}>{u.descricao}</span>
                    {u.fraciona && <small style={{ fontSize: 10, color: '#0369a1', border: '1px solid #7dd3fc',
                      borderRadius: 3, padding: '0 4px' }}>fraciona</small>}
                    {!u.ativo && <small style={{ fontSize: 11 }}>(inativo)</small>}
                    <div className={s.unidadeAcoes}>
                      <button className={s.btnIcone} title="Editar"
                        onClick={e => { e.stopPropagation(); handleEditar(u); }}>✎</button>
                      <button className={s.btnIcone}
                        title={u.ativo ? 'Inativar' : 'Ativar'}
                        style={{ color: u.ativo ? '#c00' : '#060' }}
                        onClick={e => { e.stopPropagation(); toggle.mutate(u.id); }}>
                        {u.ativo ? '✕' : '✓'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Formulário de inclusão/edição */}
            <div className={s.unidadesForm}>
              <div className={s.unidadesFormTitle}>{modoForm === 'novo' ? 'Nova Unidade' : 'Editar Unidade'}</div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 70 }}>Código</span>
                <input className={s.inputErp} style={{ width: 80 }}
                  value={editForm.codigo} maxLength={10} placeholder="UN"
                  onChange={e => setEditForm(f => ({ ...f, codigo: e.target.value.toUpperCase() }))} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 70 }}>Descrição</span>
                <input className={s.inputErp} style={{ flex: 1 }}
                  value={editForm.descricao} placeholder="Unidade"
                  onChange={e => setEditForm(f => ({ ...f, descricao: e.target.value }))} />
              </div>
              <label className={s.campo} style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Unidade fracionável (litros, quilos, metros...): as quantidades de estoque exibem casas decimais">
                <input type="checkbox" checked={editForm.fraciona}
                  onChange={e => setEditForm(f => ({ ...f, fraciona: e.target.checked }))} />
                <span style={{ fontSize: 12 }}>Fraciona (exibe casas decimais)</span>
              </label>
              <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                  onClick={handleSalvarForm} disabled={criar.isPending || atualizar.isPending}>
                  {modoForm === 'novo' ? 'Incluir' : 'Salvar'}
                </button>
                {modoForm === 'editar' && (
                  <button className={s.btnErp} onClick={handleCancelarForm}>Cancelar</button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
            disabled={!selecionado}
            onClick={() => selecionado && onSelect(selecionado.codigo)}>
            Selecionar
          </button>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-aba Entrada (ICMS / PIS / COFINS entrada)
   ──────────────────────────────────────────────────────── */
function SubAbaEntrada({ form, setForm, editando }) {
  const fe = form.fiscal_entrada || {};
  const setFe = (k, v) => setForm(f => ({ ...f, fiscal_entrada: { ...f.fiscal_entrada, [k]: v } }));
  const icmsOn = !!fe.icms_habilitado;
  const pisOn  = !!fe.pis_habilitado;
  const cofOn  = !!fe.cofins_habilitado;
  const dis = (on) => !on || !editando;

  return (
    <div className={s.fiscalBody}>

      <fieldset className={s.fieldset}>
        <legend className={s.legend}>
          <input type="checkbox" checked={icmsOn} disabled={!editando}
            onChange={e => setFe('icms_habilitado', e.target.checked)} style={{ marginRight: 5 }} />
          ICMS
        </legend>
        <div className={s.campo} style={{ opacity: icmsOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Sit. trib.</span>
          <select className={s.selectErp} style={{ flex: 1 }}
            disabled={dis(icmsOn)} value={fe.icms_cst || ''}
            onChange={e => setFe('icms_cst', e.target.value)}>
            <option value="">— selecione —</option>
            {CSTS_ICMS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div className={s.fiscalSubLegend}>Normal</div>
        <div className={s.campo} style={{ opacity: icmsOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Mod.</span>
          <select className={s.selectErp} style={{ width: 230 }}
            disabled={dis(icmsOn)} value={fe.icms_mod_bc ?? ''}
            onChange={e => setFe('icms_mod_bc', e.target.value)}>
            <option value="">— selecione —</option>
            {MODS_BC_ICMS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <span className={s.fiscalLabel}>Red. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fe.icms_red_bc_perc ?? ''}
            onChange={e => setFe('icms_red_bc_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fe.icms_aliq_perc ?? ''}
            onChange={e => setFe('icms_aliq_perc', e.target.value)} />
        </div>
        <div className={s.fiscalSubLegend}>ST (Substituição Tributária)</div>
        <div className={s.campo} style={{ opacity: icmsOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Mod.</span>
          <select className={s.selectErp} style={{ width: 230 }}
            disabled={dis(icmsOn)} value={fe.icms_st_mod_bc ?? ''}
            onChange={e => setFe('icms_st_mod_bc', e.target.value)}>
            <option value="">— selecione —</option>
            {MODS_BC_ICMS_ST.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <span className={s.fiscalLabel}>Red. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fe.icms_st_red_bc_perc ?? ''}
            onChange={e => setFe('icms_st_red_bc_perc', e.target.value)} />
          <span className={s.fiscalLabel}>MVA %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0"
            disabled={dis(icmsOn)} value={fe.icms_st_mva_perc ?? ''}
            onChange={e => setFe('icms_st_mva_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fe.icms_st_aliq_perc ?? ''}
            onChange={e => setFe('icms_st_aliq_perc', e.target.value)} />
        </div>
        <div className={s.fiscalSubLegend}>ST retido anteriormente</div>
        <div className={s.campo} style={{ opacity: icmsOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>B.C. R$</span>
          <input className={s.inputErp} style={{ width: 110 }} type="number" step="0.01" min="0"
            disabled={dis(icmsOn)} value={fe.icms_st_ret_bc ?? ''}
            onChange={e => setFe('icms_st_ret_bc', e.target.value)} />
          <span className={s.fiscalLabel}>Valor R$</span>
          <input className={s.inputErp} style={{ width: 110 }} type="number" step="0.01" min="0"
            disabled={dis(icmsOn)} value={fe.icms_st_ret_valor ?? ''}
            onChange={e => setFe('icms_st_ret_valor', e.target.value)} />
        </div>
      </fieldset>

      <fieldset className={s.fieldset}>
        <legend className={s.legend}>
          <input type="checkbox" checked={pisOn} disabled={!editando}
            onChange={e => setFe('pis_habilitado', e.target.checked)} style={{ marginRight: 5 }} />
          PIS
        </legend>
        <div className={s.campo} style={{ opacity: pisOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Sit. trib.</span>
          <select className={s.selectErp} style={{ flex: 1 }}
            disabled={dis(pisOn)} value={fe.pis_cst || ''}
            onChange={e => setFe('pis_cst', e.target.value)}>
            <option value="">— selecione —</option>
            {CSTS_PIS_COFINS_ENT.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div className={s.campo} style={{ opacity: pisOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(pisOn)} value={fe.pis_aliq_perc ?? ''}
            onChange={e => setFe('pis_aliq_perc', e.target.value)} />
        </div>
      </fieldset>

      <fieldset className={s.fieldset}>
        <legend className={s.legend}>
          <input type="checkbox" checked={cofOn} disabled={!editando}
            onChange={e => setFe('cofins_habilitado', e.target.checked)} style={{ marginRight: 5 }} />
          COFINS
        </legend>
        <div className={s.campo} style={{ opacity: cofOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Sit. trib.</span>
          <select className={s.selectErp} style={{ flex: 1 }}
            disabled={dis(cofOn)} value={fe.cofins_cst || ''}
            onChange={e => setFe('cofins_cst', e.target.value)}>
            <option value="">— selecione —</option>
            {CSTS_PIS_COFINS_ENT.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div className={s.campo} style={{ opacity: cofOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(cofOn)} value={fe.cofins_aliq_perc ?? ''}
            onChange={e => setFe('cofins_aliq_perc', e.target.value)} />
        </div>
      </fieldset>

      <p className={s.fiscalRodape}>
        Será utilizada a configuração fiscal padrão para os itens não configurados.{' '}
        <span className={s.fiscalLink}>Configuração fiscal padrão...</span>
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-aba Saída (ICMS / PIS / COFINS saída)
   ──────────────────────────────────────────────────────── */
function SubAbaSaida({ form, setForm, editando }) {
  const fs = form.fiscal_saida || {};
  const setFs = (k, v) => setForm(f => ({ ...f, fiscal_saida: { ...f.fiscal_saida, [k]: v } }));
  const icmsOn = !!fs.icms_habilitado;
  const pisOn  = !!fs.pis_habilitado;
  const cofOn  = !!fs.cofins_habilitado;
  const dis = (on) => !on || !editando;

  return (
    <div className={s.fiscalBody}>

      <fieldset className={s.fieldset}>
        <legend className={s.legend}>
          <input type="checkbox" checked={icmsOn} disabled={!editando}
            onChange={e => setFs('icms_habilitado', e.target.checked)} style={{ marginRight: 5 }} />
          ICMS
        </legend>
        <div className={s.campo} style={{ opacity: icmsOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Sit. trib.</span>
          <select className={s.selectErp} style={{ flex: 1 }}
            disabled={dis(icmsOn)} value={fs.icms_cst || ''}
            onChange={e => setFs('icms_cst', e.target.value)}>
            <option value="">— selecione —</option>
            {CSTS_ICMS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div className={s.fiscalSubLegend}>Normal</div>
        <div className={s.campo} style={{ opacity: icmsOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Mod.</span>
          <select className={s.selectErp} style={{ width: 230 }}
            disabled={dis(icmsOn)} value={fs.icms_mod_bc ?? ''}
            onChange={e => setFs('icms_mod_bc', e.target.value)}>
            <option value="">— selecione —</option>
            {MODS_BC_ICMS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <span className={s.fiscalLabel}>Red. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fs.icms_red_bc_perc ?? ''}
            onChange={e => setFs('icms_red_bc_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fs.icms_aliq_perc ?? ''}
            onChange={e => setFs('icms_aliq_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Aliq. efet. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fs.icms_aliq_efet_perc ?? ''}
            onChange={e => setFs('icms_aliq_efet_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Dif. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fs.icms_dif_perc ?? ''}
            onChange={e => setFs('icms_dif_perc', e.target.value)} />
        </div>
        <div className={s.fiscalSubLegend}>ST (Substituição Tributária)</div>
        <div className={s.campo} style={{ opacity: icmsOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Mod.</span>
          <select className={s.selectErp} style={{ width: 230 }}
            disabled={dis(icmsOn)} value={fs.icms_st_mod_bc ?? ''}
            onChange={e => setFs('icms_st_mod_bc', e.target.value)}>
            <option value="">— selecione —</option>
            {MODS_BC_ICMS_ST.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <span className={s.fiscalLabel}>Red. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fs.icms_st_red_bc_perc ?? ''}
            onChange={e => setFs('icms_st_red_bc_perc', e.target.value)} />
          <span className={s.fiscalLabel}>MVA %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0"
            disabled={dis(icmsOn)} value={fs.icms_st_mva_perc ?? ''}
            onChange={e => setFs('icms_st_mva_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 64 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(icmsOn)} value={fs.icms_st_aliq_perc ?? ''}
            onChange={e => setFs('icms_st_aliq_perc', e.target.value)} />
        </div>
        <div className={s.fiscalSubLegend}>ST retido anteriormente</div>
        <div className={s.campo} style={{ opacity: icmsOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>B.C. R$</span>
          <input className={s.inputErp} style={{ width: 110 }} type="number" step="0.01" min="0"
            disabled={dis(icmsOn)} value={fs.icms_st_ret_bc ?? ''}
            onChange={e => setFs('icms_st_ret_bc', e.target.value)} />
          <span className={s.fiscalLabel}>Valor R$</span>
          <input className={s.inputErp} style={{ width: 110 }} type="number" step="0.01" min="0"
            disabled={dis(icmsOn)} value={fs.icms_st_ret_valor ?? ''}
            onChange={e => setFs('icms_st_ret_valor', e.target.value)} />
        </div>
      </fieldset>

      <fieldset className={s.fieldset}>
        <legend className={s.legend}>
          <input type="checkbox" checked={pisOn} disabled={!editando}
            onChange={e => setFs('pis_habilitado', e.target.checked)} style={{ marginRight: 5 }} />
          PIS
        </legend>
        <div className={s.campo} style={{ opacity: pisOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Sit. trib.</span>
          <select className={s.selectErp} style={{ flex: 1 }}
            disabled={dis(pisOn)} value={fs.pis_cst || ''}
            onChange={e => setFs('pis_cst', e.target.value)}>
            <option value="">— selecione —</option>
            {CSTS_PIS_COFINS_SAI.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div className={s.campo} style={{ opacity: pisOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(pisOn)} value={fs.pis_aliq_perc ?? ''}
            onChange={e => setFs('pis_aliq_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Nat. rec.</span>
          <input className={s.inputErp} style={{ width: 60 }} maxLength={3}
            disabled={dis(pisOn)} value={fs.pis_nat_rec || ''}
            onChange={e => setFs('pis_nat_rec', e.target.value)} />
          <button className={s.btnLookup} disabled={dis(pisOn)} title="Buscar natureza da receita">...</button>
        </div>
      </fieldset>

      <fieldset className={s.fieldset}>
        <legend className={s.legend}>
          <input type="checkbox" checked={cofOn} disabled={!editando}
            onChange={e => setFs('cofins_habilitado', e.target.checked)} style={{ marginRight: 5 }} />
          COFINS
        </legend>
        <div className={s.campo} style={{ opacity: cofOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Sit. trib.</span>
          <select className={s.selectErp} style={{ flex: 1 }}
            disabled={dis(cofOn)} value={fs.cofins_cst || ''}
            onChange={e => setFs('cofins_cst', e.target.value)}>
            <option value="">— selecione —</option>
            {CSTS_PIS_COFINS_SAI.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div className={s.campo} style={{ opacity: cofOn ? 1 : 0.5 }}>
          <span className={s.campoLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01" min="0" max="100"
            disabled={dis(cofOn)} value={fs.cofins_aliq_perc ?? ''}
            onChange={e => setFs('cofins_aliq_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Nat. rec.</span>
          <input className={s.inputErp} style={{ width: 60 }} maxLength={3}
            disabled={dis(cofOn)} value={fs.cofins_nat_rec || ''}
            onChange={e => setFs('cofins_nat_rec', e.target.value)} />
          <button className={s.btnLookup} disabled={dis(cofOn)} title="Buscar natureza da receita">...</button>
        </div>
      </fieldset>

      <fieldset className={s.fieldset}>
        <legend className={s.legend}>
          IBS / CBS — Reforma Tributária
          <span style={{ fontWeight: 'normal', fontSize: 11, marginLeft: 6, opacity: 0.75 }}>
            (NT 2025.002 — obrigatório a partir de 01/01/2027)
          </span>
        </legend>
        <div className={s.campo}>
          <span className={s.campoLabel}>CST IBS/CBS</span>
          <select className={s.selectErp} style={{ flex: 1 }}
            disabled={!editando} value={fs.ibscbs_cst || ''}
            onChange={e => setFs('ibscbs_cst', e.target.value)}>
            <option value="">— selecione —</option>
            {CSTS_IBSCBS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
          <span className={s.fiscalLabel}>cClassTrib</span>
          <input className={s.inputErp} style={{ width: 80 }} maxLength={6} placeholder="000000"
            disabled={!editando} value={fs.ibscbs_cclasstrib || ''}
            onChange={e => setFs('ibscbs_cclasstrib', e.target.value)} />
        </div>
        <div className={s.fiscalSubLegend}>IBS</div>
        <div className={s.campo}>
          <span className={s.campoLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.0001" min="0"
            disabled={!editando} value={fs.ibs_aliq_perc ?? ''}
            onChange={e => setFs('ibs_aliq_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Red. BC %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01" min="0" max="100"
            disabled={!editando} value={fs.ibs_red_bc_perc ?? ''}
            onChange={e => setFs('ibs_red_bc_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Diferimento %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01" min="0" max="100"
            disabled={!editando} value={fs.ibs_dif_perc ?? ''}
            onChange={e => setFs('ibs_dif_perc', e.target.value)} />
          <span className={s.fiscalLabel}>UF destino</span>
          <input className={s.inputErp} style={{ width: 50 }} maxLength={2} placeholder="UF"
            disabled={!editando} value={fs.ibs_uf_destino || ''}
            onChange={e => setFs('ibs_uf_destino', e.target.value.toUpperCase())} />
        </div>
        <div className={s.campo}>
          <span className={s.campoLabel}>Créd. presumido — cód.</span>
          <input className={s.inputErp} style={{ width: 100 }} maxLength={10}
            disabled={!editando} value={fs.ibs_cred_pres_codigo || ''}
            onChange={e => setFs('ibs_cred_pres_codigo', e.target.value)} />
          <span className={s.fiscalLabel}>%</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.0001" min="0"
            disabled={!editando} value={fs.ibs_cred_pres_perc ?? ''}
            onChange={e => setFs('ibs_cred_pres_perc', e.target.value)} />
        </div>
        <div className={s.fiscalSubLegend}>CBS</div>
        <div className={s.campo}>
          <span className={s.campoLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.0001" min="0"
            disabled={!editando} value={fs.cbs_aliq_perc ?? ''}
            onChange={e => setFs('cbs_aliq_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Red. BC %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01" min="0" max="100"
            disabled={!editando} value={fs.cbs_red_bc_perc ?? ''}
            onChange={e => setFs('cbs_red_bc_perc', e.target.value)} />
          <span className={s.fiscalLabel}>Diferimento %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01" min="0" max="100"
            disabled={!editando} value={fs.cbs_dif_perc ?? ''}
            onChange={e => setFs('cbs_dif_perc', e.target.value)} />
        </div>
        <div className={s.campo}>
          <span className={s.campoLabel}>Créd. presumido — cód.</span>
          <input className={s.inputErp} style={{ width: 100 }} maxLength={10}
            disabled={!editando} value={fs.cbs_cred_pres_codigo || ''}
            onChange={e => setFs('cbs_cred_pres_codigo', e.target.value)} />
          <span className={s.fiscalLabel}>%</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.0001" min="0"
            disabled={!editando} value={fs.cbs_cred_pres_perc ?? ''}
            onChange={e => setFs('cbs_cred_pres_perc', e.target.value)} />
        </div>
        <div className={s.fiscalSubLegend}>Imposto Seletivo (IS)</div>
        <div className={s.campo}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <input type="checkbox" checked={!!fs.is_incide} disabled={!editando}
              onChange={e => setFs('is_incide', e.target.checked)} />
            Incide
          </label>
          <span className={s.fiscalLabel}>Aliq. %</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.0001" min="0"
            disabled={!editando || !fs.is_incide} value={fs.is_aliq_perc ?? ''}
            onChange={e => setFs('is_aliq_perc', e.target.value)} />
        </div>
      </fieldset>

      <p className={s.fiscalRodape}>
        Será utilizada a configuração fiscal padrão para os itens não configurados.{' '}
        <span className={s.fiscalLink}>Configuração fiscal padrão...</span>
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-aba FCP por UF
   ──────────────────────────────────────────────────────── */
function SubAbaFcpUf({ form, setForm, editando }) {
  const fcpMap = Object.fromEntries((form.fcp_uf || []).map(f => [f.uf, f.fcp_perc]));
  const setFcp = (uf, valor) => setForm(f => {
    const lst = [...(f.fcp_uf || [])];
    const i = lst.findIndex(x => x.uf === uf);
    if (i >= 0) lst[i] = { uf, fcp_perc: valor };
    else lst.push({ uf, fcp_perc: valor });
    return { ...f, fcp_uf: lst };
  });

  return (
    <div className={s.fiscalBody}>
      <table className={s.tabelaFcp}>
        <thead>
          <tr>
            <th>Estado</th>
            <th style={{ width: 50 }}>UF</th>
            <th style={{ width: 90, textAlign: 'right' }}>FCP %</th>
          </tr>
        </thead>
        <tbody>
          {ESTADOS.map(estado => (
            <tr key={estado.uf}>
              <td>{estado.nome}</td>
              <td className={s.fcpUf}>{estado.uf}</td>
              <td style={{ textAlign: 'right' }}>
                <input className={s.inputErp} style={{ width: 70, textAlign: 'right' }}
                  type="number" step="0.01" min="0" max="100"
                  disabled={!editando}
                  value={fcpMap[estado.uf] ?? '0.00'}
                  onChange={evt => setFcp(estado.uf, evt.target.value)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Tabela de Preço (read-only no cadastro de produtos)
   ──────────────────────────────────────────────────────── */
function AbaPrecos({ produtoId }) {
  const { data: tabelas = [], isLoading } = useQuery({
    queryKey: ['produto-tabelas-preco', produtoId],
    queryFn: () => api.get(`/tabelas-preco/por-produto/${produtoId}`).then(r => r.data),
    enabled: !!produtoId,
  });

  if (!produtoId) {
    return (
      <div style={{ padding: 20, color: '#888', fontStyle: 'italic', fontSize: 13 }}>
        Salve o produto para visualizar as tabelas de preço vinculadas.
      </div>
    );
  }
  if (isLoading) return <div style={{ padding: 20, color: '#888' }}>Carregando...</div>;

  const fmt = v => (parseFloat(v) || 0).toFixed(2);

  return (
    <div style={{ padding: 4 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ background: '#d4d0c8', border: '1px solid #999', padding: '3px 8px', width: 70 }}>Cód.</th>
            <th style={{ background: '#d4d0c8', border: '1px solid #999', padding: '3px 8px' }}>Nome da Tabela</th>
            <th style={{ background: '#d4d0c8', border: '1px solid #999', padding: '3px 8px', width: 80 }}>Base</th>
            <th style={{ background: '#d4d0c8', border: '1px solid #999', padding: '3px 8px', width: 110, textAlign: 'right' }}>(-/+)%</th>
            <th style={{ background: '#d4d0c8', border: '1px solid #999', padding: '3px 8px', width: 120, textAlign: 'right' }}>Preço na Tabela R$</th>
          </tr>
        </thead>
        <tbody>
          {tabelas.map((t, i) => {
            const usaCusto = t.base_calculo === 'custo';
            const base = parseFloat(usaCusto ? t.preco_custo : t.preco_venda) || 0;
            const precoTab = t.preco_fixo != null
              ? fmt(t.preco_fixo)
              : fmt(base * (1 + (parseFloat(t.percentual) || 0) / 100));
            const percExib = t.preco_fixo != null
              ? (base ? (((parseFloat(t.preco_fixo) / base) - 1) * 100).toFixed(4) : '0.0000')
              : fmt(parseFloat(t.percentual) || 0);
            return (
              <tr key={t.id} style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                <td style={{ border: '1px solid #ddd', padding: '3px 8px' }}>{t.codigo}</td>
                <td style={{ border: '1px solid #ddd', padding: '3px 8px' }}>{t.descricao}</td>
                <td style={{ border: '1px solid #ddd', padding: '3px 8px' }}>{usaCusto ? 'Custo' : 'Venda'}</td>
                <td style={{ border: '1px solid #ddd', padding: '3px 8px', textAlign: 'right' }}>{percExib}</td>
                <td style={{ border: '1px solid #ddd', padding: '3px 8px', textAlign: 'right', fontWeight: 'bold' }}>{precoTab}</td>
              </tr>
            );
          })}
          {!tabelas.length && (
            <tr>
              <td colSpan={5} style={{ border: '1px solid #ddd', padding: 12, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                Este produto não está vinculado a nenhuma tabela de preço.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {!!tabelas.length && (
        <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontStyle: 'italic' }}>
          Gerencie as tabelas em Cadastro &rsaquo; Tabela de Preços.
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Fiscal
   ──────────────────────────────────────────────────────── */
function AbaFiscal({ form, setForm, editando, unidades }) {
  const [subAba, setSubAba] = useState('geral');
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));
  const ehCombustivel = !!(form.cod_anp?.trim());

  return (
    <div className={s.abaFiscalWrap}>
      {/* Sub-abas internas */}
      <div className={s.subTabBar}>
        {SUB_ABAS_FISCAL.map(a => (
          <button key={a.id}
            className={`${s.subTab} ${subAba === a.id ? s.subTabAtiva : ''}`}
            onClick={() => setSubAba(a.id)}>
            {a.label}
          </button>
        ))}
      </div>

      <div className={s.subTabContent}>
        {subAba === 'geral' ? (
          <div className={s.fiscalBody}>

            {/* ─── Simples Nacional ─── */}
            <fieldset className={s.fieldset}>
              <legend className={s.legend}>Simples Nacional</legend>
              <div className={s.campo}>
                <span className={s.campoLabel}>CSOSN</span>
                <select className={s.selectErp} style={{ flex: 1 }}
                  value={form.csosn || ''} disabled={!editando}
                  onChange={e => set('csosn', e.target.value)}>
                  {CSOSN_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            </fieldset>

            {/* ─── NFS-e (só serviço) ─── */}
            {form.tipo === 'serviço' && (
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>NFS-e — Sistema Nacional</legend>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Código NBS</span>
                  <input className={s.inputErp} style={{ width: 110 }}
                    maxLength={9} placeholder="000000000"
                    value={form.servico_cnbs || ''} readOnly={!editando}
                    onChange={e => set('servico_cnbs', e.target.value.replace(/\D/g, '').slice(0, 9))} />
                </div>
                <p className={s.fiscalObs}>
                  Nomenclatura Brasileira de Serviços (9 dígitos) — obrigatória na DPS (‹cNBS›),
                  sem correspondência automática a partir do Serviço (LC 116) ainda: confira com o
                  contador ou em nfse.gov.br. Sem isso, a NFS-e deste produto não pode ser emitida.
                </p>
                <div className={s.campo} style={{ marginTop: 6 }}>
                  <span className={s.campoLabel}>Cód. Tributação Nacional</span>
                  <input className={s.inputErp} style={{ width: 140 }}
                    maxLength={6} placeholder="automático"
                    value={form.servico_ctribnac || ''} readOnly={!editando}
                    onChange={e => set('servico_ctribnac', e.target.value.replace(/\D/g, '').slice(0, 6))} />
                </div>
                <p className={s.fiscalObs}>
                  Normalmente resolve sozinho a partir do Serviço (LC 116) — só preencha se a
                  emissão da NFS-e acusar "código ambíguo" (mais de uma opção pro mesmo subitem).
                </p>
              </fieldset>
            )}

            {/* ─── Classificação ─── */}
            <fieldset className={s.fieldset}>
              <legend className={s.legend}>Classificação Fiscal</legend>

              <div className={s.campo}>
                <span className={s.campoLabel}>NCM</span>
                <input className={s.inputErp} style={{ width: 90 }}
                  maxLength={8} placeholder="00000000"
                  value={form.ncm || ''} readOnly={!editando}
                  onChange={e => set('ncm', e.target.value.replace(/\D/g, '').slice(0, 8))} />
                <span className={s.fiscalLabel}>EX</span>
                <input className={s.inputErp} style={{ width: 48 }}
                  maxLength={3} placeholder="000"
                  value={form.ex_ncm || ''} readOnly={!editando}
                  onChange={e => set('ex_ncm', e.target.value.replace(/\D/g, '').slice(0, 3))} />
                <span className={s.fiscalLabel}>CEST</span>
                <input className={s.inputErp} style={{ width: 80 }}
                  maxLength={7} placeholder="0000000"
                  value={form.cest || ''} readOnly={!editando}
                  onChange={e => set('cest', e.target.value.replace(/\D/g, '').slice(0, 7))} />
              </div>

              <div className={s.campo}>
                <span className={s.campoLabel}>Origem</span>
                <select className={s.selectErp} style={{ flex: 1 }}
                  value={form.origem_fiscal ?? '0'} disabled={!editando}
                  onChange={e => set('origem_fiscal', e.target.value)}>
                  {ORIGENS_FISCAL.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>

              <div className={s.campo}>
                <span className={s.campoLabel}>Tipo</span>
                <select className={s.selectErp} style={{ flex: 1 }}
                  value={form.tipo_fiscal ?? '00'} disabled={!editando}
                  onChange={e => set('tipo_fiscal', e.target.value)}>
                  {TIPOS_FISCAL.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>

              <div className={s.campo}>
                <span className={s.campoLabel}>EAN</span>
                <input className={s.inputErp} style={{ width: 155 }}
                  maxLength={14} value={form.ean_fiscal || ''} readOnly={!editando}
                  onChange={e => set('ean_fiscal', e.target.value)} />
                <button className={s.btnLookup} title="Ler código de barras" disabled={!editando}>▤</button>
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>EAN Trib.</span>
                <input className={s.inputErp} style={{ width: 155 }}
                  maxLength={14} value={form.ean_trib || ''} readOnly={!editando}
                  onChange={e => set('ean_trib', e.target.value)} />
                <button className={s.btnLookup} title="Ler código de barras" disabled={!editando}>▤</button>
              </div>
              <p className={s.fiscalObs}>
                Só será preenchida na NF-e/NFC-e caso ambos EAN e EAN Trib. estiverem preenchidos e o EAN Trib. tiver no máximo 13 caracteres.
              </p>

              <div className={s.campo} style={{ marginTop: 4 }}>
                <span className={s.campoLabel}>Cód. Benefício</span>
                <input className={s.inputErp} style={{ width: 130 }}
                  maxLength={10} value={form.cod_beneficio || ''} readOnly={!editando}
                  onChange={e => set('cod_beneficio', e.target.value)} />
              </div>
              <p className={s.fiscalObs}>
                Não será informado na NF-e/NFC-e caso a Sit. Trib. ICMS de saída seja 00.
              </p>
            </fieldset>

            {/* ─── Combustível ─── */}
            <fieldset className={s.fieldset} style={{ opacity: !ehCombustivel && editando ? 0.75 : 1 }}>
              <legend className={s.legend}>Combustível</legend>

              <div className={s.campo}>
                <span className={s.campoLabel}>Cód. ANP</span>
                <input className={s.inputErp} style={{ width: 90 }}
                  maxLength={9} placeholder="9 dígitos"
                  value={form.cod_anp || ''} readOnly={!editando}
                  onChange={e => set('cod_anp', e.target.value.replace(/\D/g, '').slice(0, 9))} />
                <span className={s.fiscalLabel} style={{ marginLeft: 10 }}>Descrição ANP</span>
                <input className={s.inputErp} style={{ flex: 1 }}
                  maxLength={95}
                  disabled={!ehCombustivel || !editando}
                  value={form.descricao_anp || ''}
                  onChange={e => set('descricao_anp', e.target.value)} />
              </div>

              <div className={s.campo}>
                <span className={s.campoLabel}>GLP %</span>
                <input className={s.inputErp} style={{ width: 64 }}
                  type="number" step="0.01" min="0" max="100"
                  disabled={!ehCombustivel || !editando}
                  value={form.glp_perc ?? ''}
                  onChange={e => set('glp_perc', e.target.value)} />
                <span className={s.fiscalLabel}>GLGNn %</span>
                <input className={s.inputErp} style={{ width: 64 }}
                  type="number" step="0.01" min="0" max="100"
                  disabled={!ehCombustivel || !editando}
                  value={form.glgnn_perc ?? ''}
                  onChange={e => set('glgnn_perc', e.target.value)} />
                <span className={s.fiscalLabel}>GLGNi %</span>
                <input className={s.inputErp} style={{ width: 64 }}
                  type="number" step="0.01" min="0" max="100"
                  disabled={!ehCombustivel || !editando}
                  value={form.glgni_perc ?? ''}
                  onChange={e => set('glgni_perc', e.target.value)} />
                <span className={s.fiscalLabel}>Val. Partida R$</span>
                <input className={s.inputErp} style={{ width: 84 }}
                  type="number" step="0.0001" min="0"
                  disabled={!ehCombustivel || !editando}
                  value={form.val_partida ?? ''}
                  onChange={e => set('val_partida', e.target.value)} />
                <span className={s.fiscalLabel}>Alíq. AdRem R$</span>
                <input className={s.inputErp} style={{ width: 84 }}
                  type="number" step="0.0001" min="0"
                  disabled={!ehCombustivel || !editando}
                  value={form.aliq_adrem ?? ''}
                  onChange={e => set('aliq_adrem', e.target.value)} />
              </div>

              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 'unset' }}></span>
                <input type="checkbox" id="chkIndImport"
                  disabled={!ehCombustivel || !editando}
                  checked={form.ind_importador || false}
                  onChange={e => set('ind_importador', e.target.checked)} />
                <label htmlFor="chkIndImport" className={s.fiscalCheckLabel}>Ind. import.</label>
                <span className={s.fiscalLabel} style={{ marginLeft: 10 }}>Cód. UF</span>
                <input className={s.inputErp} style={{ width: 44 }}
                  maxLength={2}
                  disabled={!ehCombustivel || !editando}
                  value={form.cod_uf_combustivel || ''}
                  onChange={e => set('cod_uf_combustivel', e.target.value.toUpperCase().slice(0, 2))} />
                <span className={s.fiscalLabel}>Orig. UF %</span>
                <input className={s.inputErp} style={{ width: 64 }}
                  type="number" step="0.01" min="0" max="100"
                  disabled={!ehCombustivel || !editando}
                  value={form.orig_uf_perc ?? ''}
                  onChange={e => set('orig_uf_perc', e.target.value)} />
                <span className={s.fiscalLabel}>Biodiesel %</span>
                <input className={s.inputErp} style={{ width: 64 }}
                  type="number" step="0.01" min="0" max="100"
                  disabled={!ehCombustivel || !editando}
                  value={form.biodiesel_perc ?? ''}
                  onChange={e => set('biodiesel_perc', e.target.value)} />
              </div>
            </fieldset>

            {/* ─── Unidade Tributável ─── */}
            <fieldset className={s.fieldset}>
              <legend className={s.legend}>Unidade Tributável</legend>
              <div className={s.checkRow}>
                <input type="checkbox" id="chkUnTrib"
                  checked={form.unidade_tributavel_diferente || false}
                  disabled={!editando}
                  onChange={e => set('unidade_tributavel_diferente', e.target.checked)} />
                <label htmlFor="chkUnTrib">Utilizar unidade tributável diferente da unidade de venda</label>
              </div>
              {form.unidade_tributavel_diferente && (
                <div className={s.campo} style={{ marginTop: 6 }}>
                  <span className={s.campoLabel}>Fator conversão</span>
                  <input className={s.inputErp} style={{ width: 90 }}
                    type="number" step="0.0001" min="0"
                    value={form.fator_conversao_trib || ''}
                    readOnly={!editando}
                    onChange={e => set('fator_conversao_trib', e.target.value)} />
                  <span className={s.fiscalLabel} style={{ marginLeft: 12 }}>Unidade tributável</span>
                  <select className={s.selectErp} style={{ width: 170 }}
                    value={form.unidade_tributavel || ''}
                    disabled={!editando}
                    onChange={e => set('unidade_tributavel', e.target.value)}>
                    <option value="">— selecione —</option>
                    {unidades.map(u => (
                      <option key={u.id} value={u.codigo}>{u.codigo} — {u.descricao}</option>
                    ))}
                  </select>
                </div>
              )}
            </fieldset>

          </div>
        ) : subAba === 'entrada' ? (
          <SubAbaEntrada form={form} setForm={setForm} editando={editando} />
        ) : subAba === 'saida' ? (
          <SubAbaSaida form={form} setForm={setForm} editando={editando} />
        ) : subAba === 'fcp' ? (
          <SubAbaFcpUf form={form} setForm={setForm} editando={editando} />
        ) : null}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Geral
   ──────────────────────────────────────────────────────── */
function AbaGeral({ form, setForm, editando, setModalBusca, setModalCodigo }) {
  const { data: setores = [] } = useQuery({
    queryKey: ['setores-estoque'],
    queryFn: () => api.get('/setores-estoque').then(r => r.data),
  });

  // Sincroniza setores quando carregam
  useEffect(() => {
    if (!setores.length) return;
    setForm(f => {
      const atuais = f.estoque_setores;
      const merged = setores.map(st => {
        const ex = atuais.find(e => e.setor_id === st.id);
        return ex || {
          setor_id: st.id, nome: st.nome, sigla: st.sigla, loja_numero: st.loja_numero || 1,
          quantidade: 0, reserva: 0, futuro: 0,
          estoque_minimo: 0, dias_minimo: 0, estoque_maximo: 0, dias_maximo: 0,
        };
      });
      const mesmos = JSON.stringify(merged) === JSON.stringify(atuais);
      return mesmos ? f : { ...f, estoque_setores: merged };
    });
  }, [setores, setForm]);

  const set = (campo, valor) => setForm(f => {
    const novo = { ...f, [campo]: valor };
    // Recalcula preço de venda automaticamente
    if (['preco_custo', 'outros_custos_pct', 'margem_pct'].includes(campo)) {
      novo.preco_venda = calcPrecoVenda(
        campo === 'preco_custo' ? valor : f.preco_custo,
        campo === 'outros_custos_pct' ? valor : f.outros_custos_pct,
        campo === 'margem_pct' ? valor : f.margem_pct,
      );
    }
    return novo;
  });

  const [modalLocais,    setModalLocais]    = useState(false);
  const [cbSelecionado,  setCbSelecionado]  = useState(null);

  // Limpa seleção ao trocar de produto
  useEffect(() => { setCbSelecionado(null); }, [form.id]);

  const atualizarEstoqueSetor = (setor_id, campo, valor) => {
    setForm(f => ({
      ...f,
      estoque_setores: f.estoque_setores.map(e =>
        e.setor_id === setor_id ? { ...e, [campo]: valor } : e
      ),
    }));
  };

  const calcSaldo = es =>
    (parseFloat(es.quantidade) || 0) - (parseFloat(es.reserva) || 0) + (parseFloat(es.futuro) || 0);

  const totais = form.estoque_setores.reduce(
    (acc, e) => ({
      quantidade: acc.quantidade + (parseFloat(e.quantidade) || 0),
      reserva:    acc.reserva    + (parseFloat(e.reserva)    || 0),
      futuro:     acc.futuro     + (parseFloat(e.futuro)     || 0),
      saldo:      acc.saldo      + calcSaldo(e),
    }),
    { quantidade: 0, reserva: 0, futuro: 0, saldo: 0 }
  );

  const removerCodigo = idx => {
    setForm(f => ({ ...f, codigos_barras: f.codigos_barras.filter((_, i) => i !== idx) }));
  };

  return (
    <div className={s.geralGrid}>
      {/* ─── Coluna esquerda ─── */}
      <div className={s.geralEsq}>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Dados Principais</legend>

          <div className={s.campo}>
            <span className={s.campoLabel}>Descr. Resumida</span>
            <input className={`${s.inputErp} ${s.inputErp}`} style={{ flex: 1 }}
              value={form.descricao_resumida} readOnly={!editando}
              onChange={e => set('descricao_resumida', e.target.value)} />
          </div>
          <div className={s.campo}>
            <span className={s.campoLabel}>Aplicação</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.aplicacao} readOnly={!editando}
              onChange={e => set('aplicacao', e.target.value)} />
          </div>
          <div className={s.campo}>
            <span className={s.campoLabel}>Observação</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.descricao} readOnly={!editando}
              onChange={e => set('descricao', e.target.value)} />
          </div>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Classificação</legend>
          {[
            { campo: 'fornecedor', label: 'Fornecedor', tipo: 'fornecedores' },
            { campo: 'marca',      label: 'Marca',      tipo: 'marcas' },
            { campo: 'grupo',      label: 'Grupo',      tipo: 'grupos' },
            { campo: 'subgrupo',   label: 'Subgrupo',   tipo: 'subgrupos' },
          ].map(({ campo, label, tipo }) => (
            <div className={s.campo} key={campo}>
              <span className={s.campoLabel}>{label}</span>
              <input className={s.inputErp} style={{ flex: 1 }}
                value={form[`${campo}_nome`]} readOnly
                placeholder={editando ? 'Clique em ... para buscar' : ''} />
              <button className={s.btnLookup} disabled={!editando}
                onClick={() => setModalBusca(tipo)}>
                ...
              </button>
            </div>
          ))}
        </fieldset>

        <fieldset className={s.fieldset} style={{ flex: 1, minHeight: 0 }}>
          <legend className={s.legend}>
            Estoque
            <button className={s.btnLookup}
              style={{ marginLeft: 8, height: 20, fontSize: 12, padding: '0 6px' }}
              onClick={() => setModalLocais(true)}
              title="Gerenciar locais de estoque">
              ⚙ Locais
            </button>
          </legend>

          <div className={s.estoqueWrap}>
            <table className={s.tabelaEstoque} style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ width: 34 }}>LJ</th>
                  <th style={{ width: 54 }}>Cod</th>
                  <th style={{ minWidth: 90 }}>NomeLocal</th>
                  <th style={{ width: 76, textAlign: 'right' }}>Estoque</th>
                  <th style={{ width: 76, textAlign: 'right' }}>Reserva</th>
                  <th style={{ width: 76, textAlign: 'right' }}>Futuro</th>
                  <th style={{ width: 76, textAlign: 'right' }}>Saldo</th>
                  <th style={{ width: 76, textAlign: 'right' }}>Estoq.Mín</th>
                  <th style={{ width: 58, textAlign: 'right' }}>Dias Mín</th>
                  <th style={{ width: 76, textAlign: 'right' }}>Estoq.Máx</th>
                  <th style={{ width: 58, textAlign: 'right' }}>Dias Máx</th>
                </tr>
              </thead>
              <tbody>
                {(form.estoque_setores.length
                  ? form.estoque_setores
                  : setores.map(st => ({ setor_id: st.id, nome: st.nome, sigla: st.sigla, loja_numero: st.loja_numero || 1, quantidade: 0, reserva: 0, futuro: 0, estoque_minimo: 0, dias_minimo: 0, estoque_maximo: 0, dias_maximo: 0 }))
                ).map(es => {
                  const saldo = calcSaldo(es);
                  const upd = (campo, val) => atualizarEstoqueSetor(es.setor_id, campo, val);
                  return (
                    <tr key={es.setor_id}>
                      <td style={{ textAlign: 'center' }}>{es.loja_numero || 1}</td>
                      <td style={{ fontWeight: 'bold' }}>{es.sigla}</td>
                      <td className={s.tdNome}>{es.nome}</td>
                      {[
                        ['quantidade',      '0.001'],
                        ['reserva',         '0.001'],
                        ['futuro',          '0.001'],
                      ].map(([campo, step]) => (
                        <td key={campo} style={{ textAlign: 'right', padding: '1px 2px' }}>
                          {editando
                            ? <input type="number" step={step} min="0" className={s.tdInput}
                                value={es[campo] ?? 0}
                                onChange={e => upd(campo, e.target.value)} />
                            : Number(es[campo] || 0).toFixed(3)}
                        </td>
                      ))}
                      <td style={{ textAlign: 'right', padding: '1px 4px' }}
                          className={saldo < 0 ? s.saldoNeg : s.saldoPos}>
                        {Number(saldo).toFixed(3)}
                      </td>
                      {[
                        ['estoque_minimo',  '0.001'],
                        ['dias_minimo',     '1'],
                        ['estoque_maximo',  '0.001'],
                        ['dias_maximo',     '1'],
                      ].map(([campo, step]) => (
                        <td key={campo} style={{ textAlign: 'right', padding: '1px 2px' }}>
                          {editando
                            ? <input type="number" step={step} min="0" className={s.tdInput}
                                value={es[campo] ?? 0}
                                onChange={e => upd(campo, e.target.value)} />
                            : campo.startsWith('dias')
                              ? Number(es[campo] || 0)
                              : Number(es[campo] || 0).toFixed(3)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className={s.tabelaTotaisRow}>
                  <td colSpan={3} style={{ paddingLeft: 6 }}>TOTAIS</td>
                  <td style={{ textAlign: 'right', paddingRight: 4 }}>{totais.quantidade.toFixed(3)}</td>
                  <td style={{ textAlign: 'right', paddingRight: 4 }}>{totais.reserva.toFixed(3)}</td>
                  <td style={{ textAlign: 'right', paddingRight: 4 }}>{totais.futuro.toFixed(3)}</td>
                  <td style={{ textAlign: 'right', paddingRight: 4 }}
                      className={totais.saldo < 0 ? s.saldoNeg : s.saldoPos}>
                    {totais.saldo.toFixed(3)}
                  </td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {modalLocais && <ModalLocaisEstoque onClose={() => { setModalLocais(false); }} />}
        </fieldset>
      </div>

      {/* ─── Coluna direita ─── */}
      <div className={s.geralDir}>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>
            Códigos de Barras
            <span style={{ fontWeight: 'normal', marginLeft: 6, color: '#666' }}>
              ({form.codigos_barras.length})
            </span>
          </legend>

          <div className={s.cbWrap}>
            <table className={s.cbTabela}>
              <colgroup>
                <col style={{ width: 20 }} />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>Código de Barras</th>
                </tr>
              </thead>
              <tbody>
                {form.codigos_barras.map((cb, i) => (
                  <tr key={i}
                    className={cbSelecionado === i ? s.cbSel : ''}
                    onClick={() => setCbSelecionado(i)}
                    onDoubleClick={() => {
                      setCbSelecionado(i);
                      if (editando) setModalCodigo({ aberto: true, indice: i, inicial: cb });
                    }}>
                    <td className={s.cbSeletor}>{cbSelecionado === i ? '►' : ''}</td>
                    <td className={s.cbCodigo}>
                      {cb.codigo}
                      {Number(cb.quantidade) > 1 && (
                        <span title="Código de embalagem: bipar no PDV lança essa quantidade"
                          style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: cbSelecionado === i ? '#fff' : '#1a56db' }}>
                          cx {String(Number(cb.quantidade)).replace('.', ',')}
                        </span>
                      )}
                      {cb.principal && (
                        <span style={{ color: cbSelecionado === i ? '#adf' : '#006600', marginLeft: 8, fontSize: 11 }}>★</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!form.codigos_barras.length && (
                  <tr>
                    <td colSpan={2} className={s.cbEmpty}>
                      Nenhum código cadastrado — clique em + para adicionar
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={s.cbBotoes}>
            <button className={s.btnErp} disabled={!form.id} style={{ fontSize: 12 }}>
              Gerar Etiqueta
            </button>
            <div style={{ flex: 1 }} />
            <button className={s.btnIcone} title="Adicionar código de barras"
              disabled={!editando}
              onClick={() => setModalCodigo({ aberto: true, indice: null, inicial: null })}>
              +
            </button>
            <button className={s.btnIcone} title="Editar código selecionado"
              disabled={!editando || cbSelecionado === null}
              onClick={() => {
                const cb = form.codigos_barras[cbSelecionado];
                if (cb) setModalCodigo({ aberto: true, indice: cbSelecionado, inicial: cb });
              }}>
              ✎
            </button>
            <button className={s.btnIcone} title="Excluir código selecionado"
              disabled={!editando || cbSelecionado === null}
              onClick={() => {
                if (cbSelecionado === null) return;
                if (!window.confirm('Excluir este código de barras?')) return;
                removerCodigo(cbSelecionado);
                setCbSelecionado(null);
              }}>
              ✕
            </button>
          </div>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Unidade de Medida</legend>
          <div className={s.campo}>
            <span className={s.campoLabel}>Preço p/ UN Med.</span>
            <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.01"
              value={form.preco_venda} readOnly title="Preço de venda calculado" />
          </div>
          <div className={s.campo}>
            <span className={s.campoLabel}>Fator Conversão</span>
            <input className={s.inputErp} style={{ width: 80 }} type="number" step="0.0001"
              value={form.fator_conversao} readOnly={!editando}
              onChange={e => set('fator_conversao', e.target.value)} />
          </div>
          <div className={s.campo}>
            <span className={s.campoLabel}>Unid. Medida</span>
            <input className={s.inputErp} style={{ width: 80 }}
              value={form.unidade_medida} readOnly={!editando}
              onChange={e => set('unidade_medida', e.target.value)} />
          </div>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Cálculo do Preço de Venda</legend>
          {[
            { campo: 'preco_custo',       label: 'Preço Custo R$', step: '0.01' },
            { campo: 'outros_custos_pct', label: 'Outros Custos %', step: '0.01' },
            { campo: 'margem_pct',        label: 'Margem %',        step: '0.01' },
          ].map(({ campo, label, step }) => (
            <div className={s.precoCalcRow} key={campo}>
              <span className={s.precoCalcLabel}>{label}</span>
              <input className={`${s.inputErp} ${s.precoCalcInput}`}
                type="number" step={step} min="0"
                value={form[campo]} readOnly={!editando}
                onChange={e => set(campo, e.target.value)} />
            </div>
          ))}
          <div style={{ borderTop: '1px solid #ccc', marginTop: 4, paddingTop: 4 }}>
            <div className={s.precoCalcRow}>
              <span className={s.precoCalcLabel} style={{ fontWeight: 'bold' }}>Preço Venda R$</span>
              <input className={`${s.inputErp} ${s.precoCalcInput} ${s.calcd}`}
                type="number" step="0.01"
                value={form.preco_venda} readOnly={!editando}
                onChange={e => set('preco_venda', e.target.value)} />
            </div>
          </div>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Comercial</legend>
          {[
            { campo: 'desconto_maximo_pct', label: 'Desc. Máximo %' },
            { campo: 'comissao_pct',        label: 'Comissão %' },
            { campo: 'peso_kg',             label: 'Peso Kg', step: '0.001' },
          ].map(({ campo, label, step = '0.01' }) => (
            <div className={s.campo} key={campo}>
              <span className={s.campoLabel}>{label}</span>
              <input className={s.inputErp} style={{ width: 80 }}
                type="number" step={step} min="0"
                value={form[campo]} readOnly={!editando}
                onChange={e => set(campo, e.target.value)} />
            </div>
          ))}
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Atributos</legend>
          {[
            { campo: 'pesado_no_caixa',        label: 'Pesado no caixa' },
            { campo: 'possui_acrescimo',        label: 'Possui acréscimo' },
            { campo: 'producao_propria',        label: 'Produção própria' },
            { campo: 'produto_balanca',         label: 'Produto de balança' },
            { campo: 'recebido_em_consignacao', label: 'Recebido em consignação' },
            // Produto "coringa": no PDV, ao selecionar este item o caixa
            // sempre para em Qtd → Preço pra digitar na hora, mesmo com o
            // lançamento guiado desligado nos Parâmetros — serve pra vender
            // algo ainda sem cadastro próprio (ver PDV.jsx: iniciarPendencia).
            { campo: 'permite_alterar_qtd_preco', label: 'Permite alterar quantidade/preço no PDV' },
            // Alimenta a Fila de Produção (tela da cozinha): comanda paga
            // com pelo menos 1 item marcado assim vira um ticket lá — item
            // sem a flag na mesma comanda fica de fora do ticket.
            { campo: 'item_cozinha', label: 'Item de Cozinha' },
          ].map(({ campo, label }) => (
            <div className={s.checkRow} key={campo}>
              <input type="checkbox" id={campo} checked={form[campo]} disabled={!editando}
                onChange={e => set(campo, e.target.checked)} />
              <label htmlFor={campo}>{label}</label>
            </div>
          ))}
        </fieldset>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Plataformas de Venda — flags de onde o produto pode aparecer
   fora do PDV (catálogo online etc.). Lista cresce aqui conforme
   novas plataformas forem integradas, sem mudar a estrutura da aba.
   ──────────────────────────────────────────────────────── */
function AbaPlataformas({ form, setForm, editando }) {
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));
  return (
    <div style={{ padding: 20 }}>
      <fieldset className={s.fieldset}>
        <legend className={s.legend}>Plataformas de Venda</legend>
        {[
          { campo: 'catalogo_online', label: 'Catálogo Online' },
        ].map(({ campo, label }) => (
          <div className={s.checkRow} key={campo}>
            <input type="checkbox" id={campo} checked={!!form[campo]} disabled={!editando}
              onChange={e => set(campo, e.target.checked)} />
            <label htmlFor={campo}>{label}</label>
          </div>
        ))}
      </fieldset>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Fornecedores — códigos do produto por fornecedor
   ──────────────────────────────────────────────────────── */
function AbaFornecedores({ produtoId }) {
  const [linhas, setLinhas]     = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [form, setForm]         = useState({ fornecedor_id: '', fornecedor_nome: '', codigo_fornecedor: '', descricao_fornecedor: '' });
  const [editId, setEditId]     = useState(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    if (!produtoId) return;
    setCarregando(true);
    try {
      const { data } = await api.get(`/produtos/${produtoId}/codigos-fornecedor`);
      setLinhas(data);
    } catch { /* silencioso */ }
    finally { setCarregando(false); }
  }, [produtoId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    api.get('/fornecedores').then(r => setFornecedores(r.data || [])).catch(() => {});
  }, []);

  const FORM_VAZIO = { fornecedor_id: '', fornecedor_nome: '', codigo_fornecedor: '', descricao_fornecedor: '' };

  const handleSalvar = async () => {
    if (!form.codigo_fornecedor.trim()) return alert('Informe o código do produto no fornecedor.');
    setSalvando(true);
    try {
      if (editId) {
        await api.put(`/produtos/${produtoId}/codigos-fornecedor/${editId}`, form);
      } else {
        await api.post(`/produtos/${produtoId}/codigos-fornecedor`, form);
      }
      setForm(FORM_VAZIO);
      setEditId(null);
      carregar();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  const handleEditar = (linha) => {
    setEditId(linha.id);
    setForm({
      fornecedor_id:       linha.fornecedor_id || '',
      fornecedor_nome:     linha.fornecedor_nome || '',
      codigo_fornecedor:   linha.codigo_fornecedor || '',
      descricao_fornecedor: linha.descricao_fornecedor || '',
    });
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Excluir este vínculo?')) return;
    try {
      await api.delete(`/produtos/${produtoId}/codigos-fornecedor/${id}`);
      carregar();
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao excluir.'); }
  };

  if (!produtoId) return (
    <div style={{ padding: 24, color: '#888', fontStyle: 'italic' }}>
      Salve o produto primeiro para vincular fornecedores.
    </div>
  );

  return (
    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }} data-enter-scope="off">
      {/* Formulário de adição/edição */}
      <fieldset style={{ border: '1px solid #ccc', borderRadius: 4, padding: '8px 12px' }}>
        <legend style={{ fontSize: 12, fontWeight: 600, padding: '0 4px' }}>
          {editId ? 'Editar vínculo' : 'Novo vínculo fornecedor'}
        </legend>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Fornecedor</label>
            <select className={s.inputErp} style={{ width: 220 }}
              value={form.fornecedor_id}
              onChange={e => {
                const forn = fornecedores.find(f => String(f.id) === e.target.value);
                setForm(f => ({ ...f, fornecedor_id: e.target.value, fornecedor_nome: forn?.nome_fantasia || forn?.razao_social || '' }));
              }}>
              <option value="">— Sem fornecedor específico —</option>
              {fornecedores.map(f => (
                <option key={f.id} value={f.id}>{f.codigo} — {f.nome_fantasia || f.razao_social}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Código no Fornecedor *</label>
            <input className={s.inputErp} style={{ width: 140 }}
              value={form.codigo_fornecedor}
              onChange={e => setForm(f => ({ ...f, codigo_fornecedor: e.target.value }))}
              placeholder="Ex: ABC-001" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Descrição no Fornecedor</label>
            <input className={s.inputErp} style={{ width: 260 }}
              value={form.descricao_fornecedor}
              onChange={e => setForm(f => ({ ...f, descricao_fornecedor: e.target.value }))}
              placeholder="Como o fornecedor chama este produto" />
          </div>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
            onClick={handleSalvar} disabled={salvando}>
            {salvando ? 'Salvando...' : editId ? 'Atualizar' : 'Adicionar'}
          </button>
          {editId && (
            <button className={s.btnErp} onClick={() => { setEditId(null); setForm(FORM_VAZIO); }}>
              Cancelar
            </button>
          )}
        </div>
      </fieldset>

      {/* Tabela de vínculos */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#e8e8e8' }}>
            <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc' }}>Fornecedor</th>
            <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc' }}>Código no Fornecedor</th>
            <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc' }}>Descrição no Fornecedor</th>
            <th style={{ padding: '4px 8px', width: 80, border: '1px solid #ccc' }}></th>
          </tr>
        </thead>
        <tbody>
          {carregando && (
            <tr><td colSpan={4} style={{ padding: 12, textAlign: 'center', color: '#888' }}>Carregando...</td></tr>
          )}
          {!carregando && linhas.length === 0 && (
            <tr><td colSpan={4} style={{ padding: 12, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
              Nenhum fornecedor vinculado. Vínculos são criados automaticamente ao importar XML de compra.
            </td></tr>
          )}
          {linhas.map(l => (
            <tr key={l.id} style={{ background: editId === l.id ? '#fffbe6' : 'white' }}>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd' }}>{l.fornecedor_nome || <em style={{ color: '#aaa' }}>Não especificado</em>}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', fontFamily: 'monospace', fontWeight: 600 }}>{l.codigo_fornecedor}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd' }}>{l.descricao_fornecedor || '—'}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                <button className={s.btnErp} style={{ fontSize: 11, padding: '1px 6px', marginRight: 4 }}
                  onClick={() => handleEditar(l)}>Ed</button>
                <button className={`${s.btnErp} ${s.btnErpPerigo}`} style={{ fontSize: 11, padding: '1px 6px' }}
                  onClick={() => handleExcluir(l.id)}>X</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AbaLotes({ produtoId }) {
  const FORM_VAZIO = { numero_lote: '', quantidade: '', data_fabricacao: '', data_vencimento: '' };
  const [linhas, setLinhas]     = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [form, setForm]         = useState(FORM_VAZIO);
  const [editId, setEditId]     = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    if (!produtoId) return;
    setCarregando(true);
    try {
      const { data } = await api.get(`/produtos/${produtoId}/lotes`);
      setLinhas(data);
    } catch { /* silencioso */ }
    finally { setCarregando(false); }
  }, [produtoId]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleSalvar = async () => {
    if (!form.data_vencimento) return alert('Informe a data de vencimento.');
    if (form.quantidade !== '' && !(parseFloat(form.quantidade) >= 0)) return alert('Quantidade inválida.');
    setSalvando(true);
    try {
      const body = {
        numero_lote: form.numero_lote || null,
        // Opcional — é só um registro manual (não é decrementado por
        // venda); o relatório de Produtos a Vencer mostra o estoque REAL
        // do produto, não este valor.
        quantidade: form.quantidade === '' ? null : parseFloat(form.quantidade),
        data_fabricacao: form.data_fabricacao || null,
        data_vencimento: form.data_vencimento,
      };
      if (editId) await api.put(`/produtos/${produtoId}/lotes/${editId}`, body);
      else await api.post(`/produtos/${produtoId}/lotes`, body);
      setForm(FORM_VAZIO);
      setEditId(null);
      carregar();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  const handleEditar = (linha) => {
    setEditId(linha.id);
    setForm({
      numero_lote: linha.numero_lote || '',
      quantidade: String(linha.quantidade ?? ''),
      data_fabricacao: linha.data_fabricacao ? String(linha.data_fabricacao).slice(0, 10) : '',
      data_vencimento: linha.data_vencimento ? String(linha.data_vencimento).slice(0, 10) : '',
    });
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Excluir este lote?')) return;
    try {
      await api.delete(`/produtos/${produtoId}/lotes/${id}`);
      carregar();
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao excluir.'); }
  };

  // Mesmo critério do card "Produtos a Vencer" do Dashboard: já vencido
  // (dias negativos) em vermelho, vencendo em até 30 dias em âmbar.
  const corLinha = (dias) => dias == null ? 'white' : dias < 0 ? '#fee2e2' : dias <= 30 ? '#fff7ed' : 'white';

  if (!produtoId) return (
    <div style={{ padding: 24, color: '#888', fontStyle: 'italic' }}>
      Salve o produto primeiro para cadastrar lotes/vencimentos.
    </div>
  );

  return (
    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }} data-enter-scope="off">
      <fieldset style={{ border: '1px solid #ccc', borderRadius: 4, padding: '8px 12px' }}>
        <legend style={{ fontSize: 12, fontWeight: 600, padding: '0 4px' }}>
          {editId ? 'Editar lote' : 'Novo lote'}
        </legend>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Nº do Lote</label>
            <input className={s.inputErp} style={{ width: 120 }}
              value={form.numero_lote}
              onChange={e => setForm(f => ({ ...f, numero_lote: e.target.value }))}
              placeholder="Opcional" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Quantidade</label>
            <input className={s.inputErp} style={{ width: 100 }} type="number" min="0" step="0.001"
              value={form.quantidade}
              onChange={e => setForm(f => ({ ...f, quantidade: e.target.value }))}
              placeholder="Opcional" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Fabricação</label>
            <input className={s.inputErp} style={{ width: 140 }} type="date"
              value={form.data_fabricacao}
              onChange={e => setForm(f => ({ ...f, data_fabricacao: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Vencimento *</label>
            <input className={s.inputErp} style={{ width: 140 }} type="date"
              value={form.data_vencimento}
              onChange={e => setForm(f => ({ ...f, data_vencimento: e.target.value }))} />
          </div>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
            onClick={handleSalvar} disabled={salvando}>
            {salvando ? 'Salvando...' : editId ? 'Atualizar' : 'Adicionar'}
          </button>
          {editId && (
            <button className={s.btnErp} onClick={() => { setEditId(null); setForm(FORM_VAZIO); }}>
              Cancelar
            </button>
          )}
        </div>
      </fieldset>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#e8e8e8' }}>
            <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc' }}>Nº Lote</th>
            <th style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ccc' }}>Quantidade</th>
            <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc' }}>Fabricação</th>
            <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc' }}>Vencimento</th>
            <th style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ccc' }}>Dias p/ vencer</th>
            <th style={{ padding: '4px 8px', width: 80, border: '1px solid #ccc' }}></th>
          </tr>
        </thead>
        <tbody>
          {carregando && (
            <tr><td colSpan={6} style={{ padding: 12, textAlign: 'center', color: '#888' }}>Carregando...</td></tr>
          )}
          {!carregando && linhas.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 12, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
              Nenhum lote cadastrado.
            </td></tr>
          )}
          {linhas.map(l => {
            const dias = l.dias_para_vencer == null ? null : parseInt(l.dias_para_vencer);
            return (
              <tr key={l.id} style={{ background: editId === l.id ? '#fffbe6' : corLinha(dias) }}>
                <td style={{ padding: '3px 8px', border: '1px solid #ddd', fontFamily: 'monospace' }}>{l.numero_lote || '—'}</td>
                <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right' }}>{l.quantidade == null ? '—' : Number(l.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}</td>
                <td style={{ padding: '3px 8px', border: '1px solid #ddd' }}>{l.data_fabricacao ? new Date(l.data_fabricacao).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}</td>
                <td style={{ padding: '3px 8px', border: '1px solid #ddd' }}>{new Date(l.data_vencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</td>
                <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right', fontWeight: dias != null && dias <= 30 ? 700 : 400, color: dias != null && dias < 0 ? '#b91c1c' : dias != null && dias <= 30 ? '#92400e' : 'inherit' }}>
                  {dias == null ? '—' : dias < 0 ? `Vencido há ${Math.abs(dias)}d` : `${dias}d`}
                </td>
                <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                  <button className={s.btnErp} style={{ fontSize: 11, padding: '1px 6px', marginRight: 4 }}
                    onClick={() => handleEditar(l)}>Ed</button>
                  <button className={`${s.btnErp} ${s.btnErpPerigo}`} style={{ fontSize: 11, padding: '1px 6px' }}
                    onClick={() => handleExcluir(l.id)}>X</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Busca de produto (p/ adicionar item na composição) ── */
function ModalBuscaProdutoComposicao({ excluirId, onSelect, onClose, titulo = 'Adicionar Item à Composição', buscaInicial = '' }) {
  const [busca, setBusca] = useState(buscaInicial);
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (busca.trim().length < 2) { setLista([]); return; }
    const t = setTimeout(async () => {
      setCarregando(true);
      try {
        const { data } = await api.get('/produtos', { params: { busca, limite: 20 } });
        setLista((data || []).filter(p => p.id !== excluirId));
      } catch { setLista([]); }
      finally { setCarregando(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [busca, excluirId]);

  return (
    <div className={s.overlay} data-enter-scope="off" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ width: 560 }}>
        <div className={s.modalTitle} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{titulo}</span>
          <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 15 }}
            onClick={onClose}>✕</button>
        </div>
        <div className={s.modalBody}>
          <input className={s.inputErp} style={{ width: '100%', boxSizing: 'border-box' }}
            placeholder="Nome, código ou código de barras…"
            value={busca} onChange={e => setBusca(e.target.value)} autoFocus />
          <div style={{ overflowY: 'auto', maxHeight: 320, border: '1px solid #ccc', marginTop: 8 }}>
            <table className={s.modalTabela}>
              <thead><tr>
                <th style={{ width: 60 }}>Cód.</th>
                <th>Descrição</th>
                <th style={{ width: 50 }}>UN</th>
                <th style={{ width: 90 }}>Custo R$</th>
              </tr></thead>
              <tbody>
                {carregando && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 12, color: '#888' }}>Buscando...</td></tr>
                )}
                {!carregando && busca.trim().length >= 2 && lista.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 12, color: '#888', fontStyle: 'italic' }}>Nenhum resultado.</td></tr>
                )}
                {lista.map(p => (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onDoubleClick={() => onSelect(p)}>
                    <td>{p.codigo_interno}</td>
                    <td>{p.nome}</td>
                    <td>{p.unidade}</td>
                    <td style={{ fontFamily: 'monospace' }}>{Number(p.preco_custo || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button className={s.btnErp} style={{ fontSize: 11, padding: '1px 8px' }}
                        onClick={() => onSelect(p)}>Selecionar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-aba "Planilha padrão" da Composição — orçamento do fornecedor em .xlsx ── */
const MATCH_PLANILHA = {
  codigo_interno: 'reconhecido pelo código interno',
  cod_fornecedor: 'reconhecido pelo código já vinculado antes',
  codigo_barras:  'reconhecido pelo código de barras',
  manual:         'vinculado manualmente (lupa)',
};

export function SubAbaPlanilhaPadrao({ produtoId, linhas, carregando, onAlterado, onVincular }) {
  const fmtBRL = v => (v === null || v === undefined || v === '') ? '' :
    'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtNum = v => (v === null || v === undefined || v === '') ? '' :
    Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  const fmtPerc = v => (v === null || v === undefined || v === '') ? '' : `${fmtNum(v)}%`;

  const inputRef = useRef(null);
  const [importando, setImportando] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, texto }
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({}); // campos da linha em edição, como texto

  const escolherArquivo = () => {
    if (linhas.length && !window.confirm(
      'Já existe uma planilha neste produto. Importar outra SUBSTITUI a atual — os itens da composição que vieram dela saem e entram os da nova. Itens incluídos à mão continuam. Continuar?'
    )) return;
    inputRef.current?.click();
  };

  const importar = async (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    setImportando(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      const { data } = await api.post(`/produtos/${produtoId}/composicao/planilha`, fd);
      setMsg({ ok: true, texto: `${data.total} linha(s) importada(s): ${data.vinculados} já reconhecida(s) no sistema, ${data.pendentes} aguardando vínculo/cadastro.` });
      onAlterado();
    } catch (err) {
      setMsg({ ok: false, texto: err.response?.data?.erro || 'Erro ao importar a planilha.' });
    } finally { setImportando(false); }
  };

  const remover = async () => {
    if (!window.confirm('Remover a planilha padrão? Os itens da composição que vieram dela também saem (itens incluídos à mão continuam).')) return;
    try {
      await api.delete(`/produtos/${produtoId}/composicao/planilha`);
      setMsg(null);
      onAlterado();
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao remover a planilha.'); }
  };

  const desvincular = async (l) => {
    if (!window.confirm(`Desvincular "${l.descricao}" de "${l.produto_nome}"? A linha volta a ficar aguardando cadastro.`)) return;
    try {
      await api.put(`/produtos/${produtoId}/composicao/planilha/${l.id}/vinculo`, { item_produto_id: null });
      onAlterado();
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao desvincular.'); }
  };

  // Edição sem separador de milhar ("12708,24"), então vírgula = decimal;
  // sem vírgula, ponto é decimal ("9.75").
  const paraTexto = v => (v === null || v === undefined || v === '') ? '' : String(Number(v)).replace('.', ',');
  const paraNumero = t => {
    const s = String(t ?? '').replace(/R\$|%|\s/g, '');
    if (!s) return null;
    const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
    return Number.isFinite(n) ? n : NaN;
  };

  const abrirEdicao = l => {
    setEditId(l.id);
    setEdit({
      codigo_produto: l.codigo_produto || '', descricao: l.descricao || '', ncm: l.ncm || '', prazo_entrega: l.prazo_entrega || '',
      quantidade: paraTexto(l.quantidade || 0), preco_unitario: paraTexto(l.preco_unitario),
      icms_perc: paraTexto(l.icms_perc), ipi_perc: paraTexto(l.ipi_perc),
      valor_com_tributo: l.valor_com_tributo == null ? '' : Number(l.valor_com_tributo).toFixed(2).replace('.', ','),
    });
  };

  // Mexeu em Qtd, Preço ou IPI: recalcula o Valor com tributo (dá pra
  // sobrescrever depois, digitando direto no campo).
  const alterarCampo = (campo, valor) => setEdit(e => {
    const n = { ...e, [campo]: valor };
    if (['quantidade', 'preco_unitario', 'ipi_perc'].includes(campo)) {
      const qtd = paraNumero(n.quantidade), preco = paraNumero(n.preco_unitario), ipi = paraNumero(n.ipi_perc) || 0;
      if (qtd > 0 && preco > 0 && Number.isFinite(ipi)) {
        n.valor_com_tributo = (Math.round(qtd * preco * (1 + ipi / 100) * 100) / 100).toFixed(2).replace('.', ',');
      }
    }
    return n;
  });

  const salvarEdicao = async () => {
    if (!String(edit.descricao || '').trim()) return alert('Informe a descrição do produto.');
    const corpo = { codigo_produto: edit.codigo_produto, descricao: edit.descricao, ncm: edit.ncm, prazo_entrega: edit.prazo_entrega };
    const rotulos = { quantidade: 'Quantidade', preco_unitario: 'Preço unitário', icms_perc: 'ICMS%', ipi_perc: 'IPI%', valor_com_tributo: 'Valor com tributo' };
    for (const [campo, rotulo] of Object.entries(rotulos)) {
      const v = paraNumero(edit[campo]);
      if (Number.isNaN(v) || (v !== null && v < 0)) return alert(`Informe um valor válido em ${rotulo}.`);
      corpo[campo] = v;
    }
    if (corpo.quantidade === null) corpo.quantidade = 0;
    try {
      await api.put(`/produtos/${produtoId}/composicao/planilha/${editId}`, corpo);
      setEditId(null);
      onAlterado();
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao salvar.'); }
  };

  const campoEdit = (campo, rotulo, largura, extra = {}) => (
    <input className={s.inputErp} style={{ width: largura, height: 26, padding: '2px 4px', fontSize: 12 }} aria-label={rotulo}
      value={edit[campo] ?? ''} onChange={e => alterarCampo(campo, e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') salvarEdicao(); if (e.key === 'Escape') setEditId(null); }} {...extra} />
  );

  const total = linhas.reduce((a, l) => a + parseFloat(l.valor_com_tributo || 0), 0);
  const vinculadas = linhas.filter(l => l.composicao_item_id).length;
  const th = { padding: '4px 6px', border: '1px solid #ccc', fontSize: 11, lineHeight: 1.2 };
  const td = { padding: '3px 6px', border: '1px solid #ddd' };
  const btnMini = { fontSize: 11, padding: '1px 6px', minWidth: 0, height: 24 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={escolherArquivo} disabled={importando}>
          {importando ? 'Importando…' : '📥 Importar planilha padrão'}
        </button>
        {linhas.length > 0 && (
          <button className={`${s.btnErp} ${s.btnErpPerigo}`} onClick={remover} disabled={importando}>Remover planilha</button>
        )}
        <input ref={inputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={importar} data-testid="arquivo-planilha" />
        <span style={{ fontSize: 11, color: '#666' }}>
          Arquivo .xlsx com as colunas da planilha padrão. Cada linha vira um item da composição; o custo unitário do item é o <strong>Valor com tributo ÷ Quantidade</strong> (ex.: 3 un. com Valor com tributo R$ 26.734,80 → custo R$ 8.911,60 cada).
        </span>
      </div>
      {msg && <div style={{ fontSize: 12, color: msg.ok ? '#15803d' : '#b91c1c' }}>{msg.ok ? '✔ ' : '✘ '}{msg.texto}</div>}
      {linhas[0]?.nome_arquivo && <div style={{ fontSize: 11, color: '#666' }}>Planilha: <strong>{linhas[0].nome_arquivo}</strong></div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1250 }}>
          <thead>
            <tr style={{ background: '#e8e8e8' }}>
              <th style={th}>Número do fornecedor</th>
              <th style={th}>Referência do produto</th>
              <th style={th}>Código do produto</th>
              <th style={{ ...th, textAlign: 'left' }}>Descrição do produto</th>
              <th style={th}>NCM</th>
              <th style={th}>Embalagem</th>
              <th style={th}>Quantidade</th>
              <th style={th}>Prazo entrega</th>
              <th style={th}>Preço unitário</th>
              <th style={th}>ICMS%</th>
              <th style={th}>IPI%</th>
              <th style={th}>Valor com tributo</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 320 }}>Produto no sistema</th>
              <th style={{ ...th, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {carregando && <tr><td colSpan={14} style={{ padding: 12, textAlign: 'center', color: '#888' }}>Carregando...</td></tr>}
            {!carregando && linhas.length === 0 && (
              <tr><td colSpan={14} style={{ padding: 12, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                Nenhuma planilha importada. Use "📥 Importar planilha padrão".
              </td></tr>
            )}
            {linhas.map(l => {
              const editandoLinha = editId === l.id;
              const semQtd = !(parseFloat(l.quantidade) > 0);
              return (
                <tr key={l.id} style={{ background: editandoLinha ? '#fffbe6' : (l.composicao_item_id ? 'white' : '#fff7ed') }}>
                  <td style={td}>{l.numero_fornecedor}</td>
                  <td style={td}>{l.referencia}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{editandoLinha ? campoEdit('codigo_produto', 'Código do produto', 90, { autoFocus: true }) : l.codigo_produto}</td>
                  <td style={td}>{editandoLinha ? campoEdit('descricao', 'Descrição do produto', '100%', { style: { width: '100%', minWidth: 180, height: 26, padding: '2px 4px', fontSize: 12, boxSizing: 'border-box' } }) : l.descricao}</td>
                  <td style={td}>{editandoLinha ? campoEdit('ncm', 'NCM', 80) : l.ncm}</td>
                  <td style={td}>{l.embalagem}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    {editandoLinha ? campoEdit('quantidade', 'Quantidade', 60) : fmtNum(l.quantidade)}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>{editandoLinha ? campoEdit('prazo_entrega', 'Prazo entrega', 70) : l.prazo_entrega}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{editandoLinha ? campoEdit('preco_unitario', 'Preço unitário', 85) : fmtBRL(l.preco_unitario)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{editandoLinha ? campoEdit('icms_perc', 'ICMS%', 50) : fmtPerc(l.icms_perc)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{editandoLinha ? campoEdit('ipi_perc', 'IPI%', 50) : fmtPerc(l.ipi_perc)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {editandoLinha ? campoEdit('valor_com_tributo', 'Valor com tributo', 90) : fmtBRL(l.valor_com_tributo)}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ flex: 1 }} title={l.item_produto_id ? MATCH_PLANILHA[l.match_tipo] || '' : ''}>
                        {l.item_produto_id
                          ? <>✔ <strong>{l.produto_codigo}</strong> — {l.produto_nome}{semQtd && <em style={{ color: '#b45309' }}> (sem quantidade, não entra na composição)</em>}</>
                          : <em style={{ color: '#b45309' }}>Aguardando cadastro</em>}
                      </span>
                      <button className={s.btnErp} style={btnMini}
                        title="Vincular a um produto cadastrado" onClick={() => onVincular(l)}>🔍</button>
                      {l.item_produto_id && (
                        <button className={s.btnErp} style={btnMini}
                          title="Desvincular" onClick={() => desvincular(l)}>✕</button>
                      )}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {editandoLinha ? (<>
                      <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ ...btnMini, marginRight: 4 }} title="Salvar (Enter)" onClick={salvarEdicao}>✓</button>
                      <button className={s.btnErp} style={btnMini} title="Cancelar (Esc)" onClick={() => setEditId(null)}>✕</button>
                    </>) : (
                      <button className={s.btnErp} style={btnMini} title="Alterar a linha (código, descrição, NCM, quantidade, prazo, preço, ICMS, IPI, valor)"
                        onClick={() => abrirEdicao(l)}>✏️</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {linhas.length > 0 && (
              <tr style={{ background: '#f3f4f6', fontWeight: 700 }}>
                <td colSpan={11} style={{ ...td, textAlign: 'right' }}>VALOR TOTAL</td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtBRL(total)}</td>
                <td colSpan={2} style={{ ...td, fontWeight: 400, fontSize: 11 }}>
                  {vinculadas} de {linhas.length} linha(s) na composição com produto
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Aba Composição (Ficha Técnica) — só p/ produto Composto ── */
function AbaFichaTecnica({ produtoId, form, setForm, editando, onEstoqueOuCustoAlterado }) {
  const fmtBRL = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtQtd = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

  const [itens, setItens]           = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [modalBusca, setModalBusca] = useState(false);
  const [novoItem, setNovoItem]     = useState(null); // { produto_id, nome, unidade, quantidade }
  const [editId, setEditId]         = useState(null);
  const [editQtd, setEditQtd]       = useState('');
  const [salvando, setSalvando]     = useState(false);

  const [qtdMontar, setQtdMontar]     = useState('1');
  const [qtdDesmontar, setQtdDesmontar] = useState('1');
  const [setorOp, setSetorOp]         = useState('');
  const [operando, setOperando]       = useState(null); // null | 'montar' | 'desmontar'
  const [msgOp, setMsgOp]             = useState(null); // { ok, texto }

  const [subAba, setSubAba]           = useState('composicao'); // 'composicao' | 'planilha'
  const [planilha, setPlanilha]       = useState([]);
  const [carregandoPlanilha, setCarregandoPlanilha] = useState(false);
  const [vinculando, setVinculando]   = useState(null); // linha da planilha aberta na lupa

  const carregar = useCallback(async () => {
    if (!produtoId) return;
    setCarregando(true);
    try {
      const { data } = await api.get(`/produtos/${produtoId}/composicao`);
      setItens(data);
    } catch { /* silencioso */ }
    finally { setCarregando(false); }
  }, [produtoId]);

  const carregarPlanilha = useCallback(async () => {
    if (!produtoId) return;
    setCarregandoPlanilha(true);
    try {
      const { data } = await api.get(`/produtos/${produtoId}/composicao/planilha`);
      setPlanilha(data);
    } catch { /* silencioso */ }
    finally { setCarregandoPlanilha(false); }
  }, [produtoId]);

  useEffect(() => { carregar(); carregarPlanilha(); }, [carregar, carregarPlanilha]);

  const planilhaAlterada = () => {
    carregar();
    carregarPlanilha();
    onEstoqueOuCustoAlterado?.(produtoId);
  };

  const vincularLinha = async (p) => {
    const linha = vinculando;
    setVinculando(null);
    try {
      await api.put(`/produtos/${produtoId}/composicao/planilha/${linha.id}/vinculo`, { item_produto_id: p.id });
      planilhaAlterada();
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao vincular.'); }
  };

  useEffect(() => {
    if (!setorOp && form.estoque_setores?.length) setSetorOp(String(form.estoque_setores[0].setor_id));
  }, [form.estoque_setores, setorOp]);

  const handleSelecionarItem = (p) => {
    setModalBusca(false);
    setNovoItem({ produto_id: p.id, nome: p.nome, unidade: p.unidade, quantidade: '1' });
  };

  const confirmarNovoItem = async () => {
    const qtd = parseFloat(novoItem.quantidade);
    if (!qtd || qtd <= 0) return alert('Informe uma quantidade válida.');
    setSalvando(true);
    try {
      await api.post(`/produtos/${produtoId}/composicao`, { item_produto_id: novoItem.produto_id, quantidade: qtd });
      setNovoItem(null);
      carregar();
      onEstoqueOuCustoAlterado?.(produtoId); // pode ter recalculado o custo do composto
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao adicionar item.'); }
    finally { setSalvando(false); }
  };

  const handleEditar = (item) => { setEditId(item.id); setEditQtd(String(item.quantidade)); };

  const salvarEdicao = async () => {
    const qtd = parseFloat(editQtd);
    if (!qtd || qtd <= 0) return alert('Informe uma quantidade válida.');
    setSalvando(true);
    try {
      await api.put(`/produtos/${produtoId}/composicao/${editId}`, { quantidade: qtd });
      setEditId(null);
      carregar();
      onEstoqueOuCustoAlterado?.(produtoId);
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao salvar.'); }
    finally { setSalvando(false); }
  };

  const handleExcluir = async (item) => {
    if (!window.confirm(`Remover "${item.nome}" da composição?`)) return;
    try {
      await api.delete(`/produtos/${produtoId}/composicao/${item.id}`);
      carregar();
      onEstoqueOuCustoAlterado?.(produtoId);
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao excluir.'); }
  };

  const montar = async () => {
    const qtd = parseFloat(qtdMontar);
    if (!qtd || qtd <= 0) return alert('Informe uma quantidade válida para montar.');
    setOperando('montar'); setMsgOp(null);
    try {
      const { data } = await api.post(`/produtos/${produtoId}/montar`, { quantidade: qtd, setor_id: setorOp || null });
      setMsgOp({ ok: true, texto: data.mensagem });
      carregar();
      onEstoqueOuCustoAlterado?.(produtoId);
    } catch (err) { setMsgOp({ ok: false, texto: err.response?.data?.erro || 'Erro ao montar.' }); }
    finally { setOperando(null); }
  };

  const desmontar = async () => {
    const qtd = parseFloat(qtdDesmontar);
    if (!qtd || qtd <= 0) return alert('Informe uma quantidade válida para desmontar.');
    setOperando('desmontar'); setMsgOp(null);
    try {
      const { data } = await api.post(`/produtos/${produtoId}/desmontar`, { quantidade: qtd, setor_id: setorOp || null });
      setMsgOp({ ok: true, texto: data.mensagem });
      carregar();
      onEstoqueOuCustoAlterado?.(produtoId);
    } catch (err) { setMsgOp({ ok: false, texto: err.response?.data?.erro || 'Erro ao desmontar.' }); }
    finally { setOperando(null); }
  };

  if (!produtoId) return (
    <div style={{ padding: 24, color: '#888', fontStyle: 'italic' }}>
      Salve o produto primeiro para montar a composição (ficha técnica).
    </div>
  );

  const totais = itens.reduce((acc, i) => ({
    estoque: acc.estoque + parseFloat(i.estoque || 0),
    custo:   acc.custo   + (i.custo_total ?? parseFloat(i.preco_custo || 0) * parseFloat(i.quantidade || 0)),
    venda:   acc.venda   + parseFloat(i.preco_venda || 0) * parseFloat(i.quantidade || 0),
  }), { estoque: 0, custo: 0, venda: 0 });
  const itensComProduto = itens.filter(i => !i.pendente);
  const pendentes = itens.length - itensComProduto.length;

  const modalVinculo = vinculando && (
    <ModalBuscaProdutoComposicao excluirId={produtoId} onSelect={vincularLinha} onClose={() => setVinculando(null)}
      titulo={`Vincular: ${vinculando.descricao}`} buscaInicial={(vinculando.descricao || '').split(/\s+/)[0] || ''} />
  );

  const barraSubAbas = (
    <div className={s.tabBar} style={{ marginBottom: 4 }}>
      {[['composicao', 'Composição'], ['planilha', `Planilha padrão${planilha.length ? ` (${planilha.length})` : ''}`]].map(([id, label]) => (
        <button key={id} type="button" className={`${s.tab} ${subAba === id ? s.tabAtiva : ''}`} onClick={() => setSubAba(id)}>
          {label}
        </button>
      ))}
    </div>
  );

  if (subAba === 'planilha') return (
    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }} data-enter-scope="off">
      {barraSubAbas}
      <SubAbaPlanilhaPadrao produtoId={produtoId} linhas={planilha} carregando={carregandoPlanilha}
        onAlterado={planilhaAlterada} onVincular={setVinculando} />
      {modalVinculo}
    </div>
  );

  return (
    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }} data-enter-scope="off">
      {barraSubAbas}
      {/* Produção: Montar / Desmontar */}
      <fieldset style={{ border: '1px solid #ccc', borderRadius: 4, padding: '8px 12px' }}>
        <legend style={{ fontSize: 12, fontWeight: 600, padding: '0 4px' }}>Produção</legend>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Local de estoque</label>
            <select className={s.inputErp} style={{ width: 200 }} value={setorOp} onChange={e => setSetorOp(e.target.value)}>
              <option value="">— Estoque geral —</option>
              {(form.estoque_setores || []).map(es => (
                <option key={es.setor_id} value={es.setor_id}>{es.sigla || es.nome}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ fontSize: 11, fontWeight: 600 }}>Qtd. a montar</label>
              <input className={s.inputErp} style={{ width: 80 }} type="number" min="0" step="1"
                value={qtdMontar} onChange={e => setQtdMontar(e.target.value)} />
            </div>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={operando !== null || !itensComProduto.length}
              onClick={montar} title={!itensComProduto.length ? 'Cadastre os itens da composição primeiro' : ''}>
              {operando === 'montar' ? 'Montando…' : '🏭 Montar'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ fontSize: 11, fontWeight: 600 }}>Qtd. a desmontar</label>
              <input className={s.inputErp} style={{ width: 80 }} type="number" min="0" step="1"
                value={qtdDesmontar} onChange={e => setQtdDesmontar(e.target.value)} />
            </div>
            <button className={`${s.btnErp} ${s.btnErpPerigo}`} disabled={operando !== null || !itensComProduto.length}
              onClick={desmontar} title={!itensComProduto.length ? 'Cadastre os itens da composição primeiro' : ''}>
              {operando === 'desmontar' ? 'Desmontando…' : '📦 Desmontar'}
            </button>
          </div>
        </div>
        {pendentes > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>
            ⚠ {pendentes} item(ns) da planilha aguardando cadastro: entram no custo, mas Montar/Desmontar não mexe no estoque deles até serem vinculados a um produto.
          </div>
        )}
        {msgOp && (
          <div style={{ marginTop: 8, fontSize: 12, color: msgOp.ok ? '#15803d' : '#b91c1c' }}>
            {msgOp.ok ? '✔ ' : '✘ '}{msgOp.texto}
          </div>
        )}
      </fieldset>

      {/* Adicionar item */}
      {!novoItem ? (
        <button className={s.btnErp} style={{ alignSelf: 'flex-start' }} onClick={() => setModalBusca(true)}>
          + Incluir item
        </button>
      ) : (
        <fieldset style={{ border: '1px solid #ccc', borderRadius: 4, padding: '8px 12px' }}>
          <legend style={{ fontSize: 12, fontWeight: 600, padding: '0 4px' }}>Incluir item na composição</legend>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div><strong>{novoItem.nome}</strong> ({novoItem.unidade})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ fontSize: 11, fontWeight: 600 }}>Quantidade *</label>
              <input className={s.inputErp} style={{ width: 90 }} type="number" min="0" step="0.001"
                value={novoItem.quantidade}
                onChange={e => setNovoItem(n => ({ ...n, quantidade: e.target.value }))} autoFocus />
            </div>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={confirmarNovoItem} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Adicionar'}
            </button>
            <button className={s.btnErp} onClick={() => setNovoItem(null)}>Cancelar</button>
          </div>
        </fieldset>
      )}

      {/* Grade de itens */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#e8e8e8' }}>
            <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc' }}>Produto</th>
            <th style={{ padding: '4px 8px', width: 80, border: '1px solid #ccc' }}>Estoque</th>
            <th style={{ padding: '4px 8px', width: 90, border: '1px solid #ccc' }}>Qtd</th>
            <th style={{ padding: '4px 8px', width: 50, border: '1px solid #ccc' }}>UN</th>
            <th style={{ padding: '4px 8px', width: 90, border: '1px solid #ccc' }}>Custo R$</th>
            <th style={{ padding: '4px 8px', width: 90, border: '1px solid #ccc' }}>Venda R$</th>
            <th style={{ padding: '4px 8px', width: 70, border: '1px solid #ccc' }}></th>
          </tr>
        </thead>
        <tbody>
          {carregando && (
            <tr><td colSpan={7} style={{ padding: 12, textAlign: 'center', color: '#888' }}>Carregando...</td></tr>
          )}
          {!carregando && itens.length === 0 && (
            <tr><td colSpan={7} style={{ padding: 12, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
              Nenhum item na composição. Use "+ Incluir item".
            </td></tr>
          )}
          {itens.map(item => item.pendente ? (
            <tr key={item.id} style={{ background: '#fff7ed' }}>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd' }}>
                {item.nome} <em style={{ color: '#b45309', fontSize: 11 }}>— aguardando cadastro</em>
              </td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right', color: '#999' }}>—</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right' }}>{fmtQtd(item.quantidade)}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd' }}></td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right' }}>{item.preco_custo == null ? '' : fmtBRL(item.preco_custo)}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right', color: '#999' }}>—</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                <button className={s.btnErp} style={{ fontSize: 11, padding: '1px 6px' }} title="Vincular a um produto cadastrado"
                  onClick={() => setVinculando(planilha.find(l => l.id === item.planilha_linha_id) || { id: item.planilha_linha_id, descricao: item.nome })}>🔍</button>
              </td>
            </tr>
          ) : (
            <tr key={item.id} style={{ background: editId === item.id ? '#fffbe6' : 'white' }}>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd' }}>
                {item.nome}
                {item.planilha_linha_id && <span title="Veio da planilha padrão — custo = Valor com tributo ÷ Qtd" style={{ marginLeft: 6, fontSize: 11, color: '#666' }}>📄 planilha</span>}
              </td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right' }}>{fmtQtd(item.estoque)}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                {editId === item.id
                  ? <input className={s.inputErp} style={{ width: 70 }} type="number" min="0" step="0.001"
                      value={editQtd} onChange={e => setEditQtd(e.target.value)} autoFocus />
                  : fmtQtd(item.quantidade)}
              </td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd' }}>{item.unidade}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right' }}>{fmtBRL(item.preco_custo)}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'right' }}>{fmtBRL(item.preco_venda)}</td>
              <td style={{ padding: '3px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                {editId === item.id ? (<>
                  <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ fontSize: 11, padding: '1px 6px', marginRight: 4 }}
                    onClick={salvarEdicao} disabled={salvando}>✓</button>
                  <button className={s.btnErp} style={{ fontSize: 11, padding: '1px 6px' }}
                    onClick={() => setEditId(null)}>✕</button>
                </>) : item.planilha_linha_id ? (
                  <button className={s.btnErp} style={{ fontSize: 11, padding: '1px 6px' }}
                    title="Item da planilha padrão — ajuste ou desvincule na aba Planilha padrão"
                    onClick={() => setSubAba('planilha')}>📄</button>
                ) : (<>
                  <button className={s.btnErp} style={{ fontSize: 11, padding: '1px 6px', marginRight: 4 }}
                    onClick={() => handleEditar(item)}>✏️</button>
                  <button className={`${s.btnErp} ${s.btnErpPerigo}`} style={{ fontSize: 11, padding: '1px 6px' }}
                    onClick={() => handleExcluir(item)}>✕</button>
                </>)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Parâmetros */}
      <div className={s.checkRow}>
        <input type="checkbox" id="chkMontarAuto" checked={form.montar_automatico_venda} disabled={!editando}
          onChange={e => setForm(f => ({ ...f, montar_automatico_venda: e.target.checked }))} />
        <label htmlFor="chkMontarAuto">Montar automaticamente ao fazer a venda</label>
      </div>
      <div className={s.checkRow}>
        <input type="checkbox" id="chkUsarCustoComp" checked={form.usar_custo_composicao} disabled={!editando}
          onChange={e => setForm(f => ({ ...f, usar_custo_composicao: e.target.checked }))} />
        <label htmlFor="chkUsarCustoComp">Usar o custo da composição como preço de custo do produto</label>
      </div>

      {/* Totais */}
      <div style={{ display: 'flex', gap: 24, fontSize: 12, borderTop: '1px solid #ccc', paddingTop: 8 }}>
        <span><strong>Totais</strong></span>
        <span>Estoque: <strong>{fmtQtd(totais.estoque)}</strong></span>
        <span>Custo R$: <strong>{fmtBRL(totais.custo)}</strong></span>
        <span>Venda R$: <strong>{fmtBRL(totais.venda)}</strong></span>
      </div>

      {modalBusca && (
        <ModalBuscaProdutoComposicao excluirId={produtoId} onSelect={handleSelecionarItem} onClose={() => setModalBusca(false)} />
      )}
      {modalVinculo}
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Componente principal
   ──────────────────────────────────────────────────────── */
export default function CadastroProdutos() {
  const qc = useQueryClient();
  const { usuario } = useAuth();
  const [aba, setAba] = useState('geral');
  const [modo, setModo] = useState('nav');          // 'nav' | 'novo' | 'editar'
  const [posicao, setPosicao] = useState(0);
  const [form, setForm] = useState(FORM_VAZIO);
  const [modalBusca, setModalBusca] = useState(null);  // null | 'fornecedores' | 'marcas' | 'grupos' | 'subgrupos'
  const [modalCompras, setModalCompras] = useState(false);
  const {
    modalF3: modalBuscaProd, setModalF3: setModalBuscaProd,
    filtrosF3, setFiltrosF3, setFiltro, produtosF3, setProdutosF3,
    buscandoF3, idxF3, setIdxF3, buscaRapidaF3, setBuscaRapidaF3,
    buscaRapidaRef, fecharF3, buscarF3, handleBuscaRapidaF3, abrirF3,
  } = useBuscaProdutosF3();
  const [modalCodigo, setModalCodigo] = useState({ aberto: false, indice: null, inicial: null });
  const [modalUnidades, setModalUnidades] = useState(false);
  const [verTodosServicos, setVerTodosServicos] = useState(false);

  const editando = modo !== 'nav';

  // Lista de Serviços (LC 116) — sugerida pelos CNAEs da organização, com
  // opção de ver a lista completa (a sugestão é heurística, não trava nada)
  const { data: servicosSugeridos } = useQuery({
    queryKey: ['servicos-lc116-sugeridos', usuario?.organizacao_id],
    queryFn: () => api.get(`/organizacoes/${usuario.organizacao_id}/servicos-lc116-sugeridos`).then(r => r.data),
    enabled: !!usuario?.organizacao_id && form.tipo === 'serviço' && !verTodosServicos,
  });
  const { data: servicosTodos } = useQuery({
    queryKey: ['servicos-lc116-todos'],
    queryFn: () => api.get('/organizacoes/servicos-lc116').then(r => r.data),
    enabled: form.tipo === 'serviço' && (verTodosServicos || servicosSugeridos?.filtrado === false),
  });
  const listaServicosLc116 = (verTodosServicos || servicosSugeridos?.filtrado === false)
    ? (servicosTodos || [])
    : (servicosSugeridos?.itens || []);

  // Unidades de medida (para o select UN)
  const { data: unidades = [] } = useQuery({
    queryKey: ['unidades'],
    queryFn: () => api.get('/unidades').then(r => r.data),
  });

  // Lista de produtos para navegação
  const { data: listaCompleta = [] } = useQuery({
    queryKey: ['produtos-lista'],
    queryFn: () => api.get('/produtos', { params: { limite: 9999 } }).then(r => r.data),
  });
  // Seleção feita no F3 (Ok — usar selecionados): a navegação |< < > >|
  // percorre só esses produtos até limpar o filtro.
  const [filtroNav, setFiltroNav] = useState(null); // null | [{ id, codigo_interno, nome }]
  const lista = filtroNav || listaCompleta;

  // Carrega produto completo
  const carregarProduto = useCallback(async (id) => {
    if (!id) return;
    const { data } = await api.get(`/produtos/${id}`);
    setForm(produtoParaForm(data));
    setModo('nav');
  }, []);

  // Montar/Desmontar e a composição (ficha técnica) mudam o estoque e,
  // se "usar custo da composição" estiver ligado, o preco_custo do composto
  // — direto no banco, por fora do form. Refaz só esses 2 campos (em vez de
  // recarregar o form inteiro) pra não descartar uma edição em andamento
  // (ex.: preço de venda digitado mas ainda não salvo) nas outras abas.
  const atualizarEstoqueECusto = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/produtos/${id}`);
      setForm(f => ({ ...f, estoque_setores: data.estoque_setores ? comQtdOriginal(data.estoque_setores) : f.estoque_setores, preco_custo: data.preco_custo ?? f.preco_custo }));
    } catch { /* silencioso */ }
  }, []);

  // Sincroniza quando a lista muda e há um produto selecionado
  useEffect(() => {
    if (lista.length && !form.id) {
      carregarProduto(lista[0].id);
    }
  }, [lista]); // eslint-disable-line

  // Aba Composição só existe pra produto Composto — some se desmarcar o checkbox.
  useEffect(() => {
    if (aba === 'ficha' && !form.composto) setAba('geral');
  }, [form.composto, aba]);

  /* ── Ações ── */
  const handleNovo = async () => {
    try {
      const { data } = await api.get('/produtos/proximo-codigo');
      setForm({ ...FORM_VAZIO, codigo_interno: data.proximo });
      setModo('novo');
    } catch {
      setForm({ ...FORM_VAZIO, codigo_interno: 'Automático' });
      setModo('novo');
    }
  };

  const handleAlterar = () => {
    if (!form.id) return alert('Selecione um produto antes de alterar.');
    setModo('editar');
  };

  const handleCancelar = () => {
    if (form.id) carregarProduto(form.id);
    else setForm(FORM_VAZIO);
    setModo('nav');
  };

  const criarMutation = useMutation({
    mutationFn: d => api.post('/produtos', d),
    onSuccess: async ({ data }) => {
      setFiltroNav(null);
      qc.invalidateQueries(['produtos-lista']);
      await carregarProduto(data.id);
      const idx = lista.findIndex(p => p.id === data.id);
      if (idx >= 0) setPosicao(idx);
      setModo('nav');
    },
    onError: err => alert('Erro ao salvar: ' + (err.response?.data?.erros?.[0]?.msg || err.message)),
  });

  const atualizarMutation = useMutation({
    mutationFn: ({ id, d }) => api.put(`/produtos/${id}`, d),
    onSuccess: async (_, { id }) => {
      qc.invalidateQueries(['produtos-lista']);
      await carregarProduto(id);
      setModo('nav');
    },
    onError: err => alert('Erro ao salvar: ' + (err.response?.data?.erro || err.message)),
  });

  const handleSalvar = () => {
    if (!form.nome?.trim()) return alert('Informe a descrição do produto.');
    const payload = {
      nome: form.nome,
      descricao: form.descricao,
      descricao_resumida: form.descricao_resumida,
      aplicacao: form.aplicacao,
      unidade: form.unidade,
      tipo: form.tipo,
      servico_lc116_codigo: form.tipo === 'serviço' ? (form.servico_lc116_codigo || null) : null,
      servico_ctribnac: form.tipo === 'serviço' ? (form.servico_ctribnac || null) : null,
      servico_cnbs: form.tipo === 'serviço' ? (form.servico_cnbs || null) : null,
      duracao_min: form.tipo === 'serviço' ? (form.duracao_min || null) : null,
      inativo: form.inativo,
      marcador: form.marcador,
      composto: form.composto,
      montar_automatico_venda: form.montar_automatico_venda,
      usar_custo_composicao: form.usar_custo_composicao,
      fornecedor_id: form.fornecedor_id,
      marca_id: form.marca_id,
      grupo_id: form.grupo_id,
      subgrupo_id: form.subgrupo_id,
      preco_custo: parseFloat(form.preco_custo) || 0,
      outros_custos_pct: parseFloat(form.outros_custos_pct) || 0,
      margem_pct: parseFloat(form.margem_pct) || 0,
      preco_venda: parseFloat(form.preco_venda) || 0,
      desconto_maximo_pct: parseFloat(form.desconto_maximo_pct) || 0,
      comissao_pct: parseFloat(form.comissao_pct) || 0,
      peso_kg: parseFloat(form.peso_kg) || 0,
      fator_conversao: parseFloat(form.fator_conversao) || 1,
      unidade_medida: form.unidade_medida,
      estoque_minimo: parseFloat(form.estoque_minimo) || 0,
      pesado_no_caixa: form.pesado_no_caixa,
      possui_acrescimo: form.possui_acrescimo,
      producao_propria: form.producao_propria,
      produto_balanca: form.produto_balanca,
      recebido_em_consignacao: form.recebido_em_consignacao,
      permite_alterar_qtd_preco: form.permite_alterar_qtd_preco,
      item_cozinha: form.item_cozinha,
      catalogo_online: form.catalogo_online,
      // Fiscal
      csosn: form.csosn || null,
      ncm: form.ncm || null,
      ex_ncm: form.ex_ncm || null,
      cest: form.cest || null,
      origem_fiscal: parseInt(form.origem_fiscal) || 0,
      tipo_fiscal: form.tipo_fiscal || '00',
      ean_fiscal: form.ean_fiscal || null,
      ean_trib: form.ean_trib || null,
      cod_beneficio: form.cod_beneficio || null,
      cod_anp: form.cod_anp || null,
      descricao_anp: form.descricao_anp || null,
      glp_perc: form.glp_perc !== '' ? parseFloat(form.glp_perc) : null,
      glgnn_perc: form.glgnn_perc !== '' ? parseFloat(form.glgnn_perc) : null,
      glgni_perc: form.glgni_perc !== '' ? parseFloat(form.glgni_perc) : null,
      val_partida: form.val_partida !== '' ? parseFloat(form.val_partida) : null,
      aliq_adrem: form.aliq_adrem !== '' ? parseFloat(form.aliq_adrem) : null,
      ind_importador: form.ind_importador || false,
      cod_uf_combustivel: form.cod_uf_combustivel || null,
      orig_uf_perc: form.orig_uf_perc !== '' ? parseFloat(form.orig_uf_perc) : null,
      biodiesel_perc: form.biodiesel_perc !== '' ? parseFloat(form.biodiesel_perc) : null,
      unidade_tributavel_diferente: form.unidade_tributavel_diferente || false,
      fator_conversao_trib: parseFloat(form.fator_conversao_trib) || 1,
      unidade_tributavel: form.unidade_tributavel || null,
      fiscal_entrada: form.fiscal_entrada,
      fiscal_saida: form.fiscal_saida,
      fcp_uf: (() => {
        const m = Object.fromEntries((form.fcp_uf || []).map(f => [f.uf, f.fcp_perc]));
        return ESTADOS.map(e => ({ uf: e.uf, fcp_perc: parseFloat(m[e.uf] ?? 0) || 0 }));
      })(),
      codigos_barras: form.codigos_barras,
      estoque_setores: form.estoque_setores.map(e => ({
        setor_id: e.setor_id, quantidade: parseFloat(e.quantidade) || 0,
        quantidade_original: e.quantidade_original != null ? parseFloat(e.quantidade_original) || 0 : null,
        reserva: parseFloat(e.reserva) || 0, futuro: parseFloat(e.futuro) || 0,
        estoque_minimo: parseFloat(e.estoque_minimo) || 0, dias_minimo: parseInt(e.dias_minimo, 10) || 0,
        estoque_maximo: parseFloat(e.estoque_maximo) || 0, dias_maximo: parseInt(e.dias_maximo, 10) || 0,
      })),
    };
    if (modo === 'novo') criarMutation.mutate(payload);
    else atualizarMutation.mutate({ id: form.id, d: payload });
  };

  // Exclusão exige senha de admin (backend valida movimentações e devolve
  // 409 antes de pedir a senha; 401 exige_admin → pedirSenhaAdmin → repete)
  const excluirComSenha = async (id) => {
    try {
      await api.delete(`/produtos/${id}`);
      return true;
    } catch (err) {
      if (!err.response?.data?.exige_admin) throw err;
      const senha = await pedirSenhaAdmin(
        `Excluir o produto "${form.nome}" exige autorização de administrador.`,
        { acao: 'excluir_produto', detalhes: `Produto #${id} — ${form.nome}` });
      if (senha === null) return false;
      await api.delete(`/produtos/${id}`, { data: { senha_admin: senha } });
      return true;
    }
  };

  const handleExcluir = async () => {
    if (!form.id) return;
    if (!window.confirm(`Excluir o produto "${form.nome}"?`)) return;
    try {
      if (!(await excluirComSenha(form.id))) return;
      qc.invalidateQueries(['produtos-lista']);
      if (filtroNav) {
        const resto = filtroNav.filter(p => p.id !== form.id);
        setFiltroNav(resto.length ? resto : null);
        setPosicao(0);
      }
      setForm(FORM_VAZIO);
      setModo('nav');
      alert('Produto excluído.');
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao excluir produto.');
    }
  };

  const clonarMutation = useMutation({
    mutationFn: id => api.post(`/produtos/${id}/clonar`),
    onSuccess: async ({ data }) => {
      setFiltroNav(null);
      qc.invalidateQueries(['produtos-lista']);
      await carregarProduto(data.id);
      alert(`Produto clonado com sucesso! Código: ${data.codigo_interno}`);
    },
  });

  const handleClonar = () => {
    if (!form.id) return;
    if (!window.confirm(`Clonar o produto "${form.nome}"?`)) return;
    clonarMutation.mutate(form.id);
  };

  const navPrimeiro = () => {
    if (lista.length && posicao !== 0) {
      setPosicao(0);
      carregarProduto(lista[0].id);
    }
  };
  const navAnterior = () => {
    if (posicao > 0) {
      const nova = posicao - 1;
      setPosicao(nova);
      carregarProduto(lista[nova].id);
    }
  };
  const navProximo = () => {
    if (posicao < lista.length - 1) {
      const nova = posicao + 1;
      setPosicao(nova);
      carregarProduto(lista[nova].id);
    }
  };
  const navUltimo = () => {
    if (lista.length && posicao !== lista.length - 1) {
      const ultima = lista.length - 1;
      setPosicao(ultima);
      carregarProduto(lista[ultima].id);
    }
  };

  // Produto escolhido no F3 (mesmo modal do PDV) — carrega pra edição/
  // consulta, em vez de adicionar a um carrinho. Escolha de UM produto volta
  // a navegar pela lista completa.
  const confirmarF3 = useCallback(async (p) => {
    setFiltroNav(null);
    const idx = listaCompleta.findIndex(l => l.id === p.id);
    setPosicao(idx >= 0 ? idx : 0);
    await carregarProduto(p.id);
    fecharF3();
  }, [listaCompleta, carregarProduto, fecharF3]);

  // Ok — usar selecionados: navegação restrita aos produtos marcados no F3
  const confirmarSelecionadosF3 = useCallback(async (selec) => {
    if (!selec.length) return;
    if (selec.length === 1) { await confirmarF3(selec[0]); return; }
    setFiltroNav(selec.map(p => ({ id: p.id, codigo_interno: p.codigo_interno, nome: p.nome })));
    setPosicao(0);
    await carregarProduto(selec[0].id);
    fecharF3();
  }, [confirmarF3, carregarProduto, fecharF3]);

  const limparFiltroNav = () => {
    const idx = listaCompleta.findIndex(l => l.id === form.id);
    setFiltroNav(null);
    setPosicao(idx >= 0 ? idx : 0);
  };

  /* ── Seleção nos modais de busca ── */
  const BUSCA_META = {
    fornecedores: { titulo: 'Fornecedor', endpoint: 'fornecedores', campo: 'fornecedor', campos: [{ key: 'nome_fantasia', label: 'Nome Fantasia' }] },
    marcas:       { titulo: 'Marca',      endpoint: 'marcas',       campo: 'marca',      campos: [{ key: 'nome', label: 'Nome' }] },
    grupos:       { titulo: 'Grupo',      endpoint: 'grupos',        campo: 'grupo',      campos: [{ key: 'nome', label: 'Nome' }] },
    subgrupos:    { titulo: 'Subgrupo',   endpoint: 'subgrupos',     campo: 'subgrupo',   campos: [{ key: 'nome', label: 'Nome' }] },
  };

  const handleSelectBusca = row => {
    const meta = BUSCA_META[modalBusca];
    if (!meta) return;
    setForm(f => ({ ...f, [`${meta.campo}_id`]: row.id, [`${meta.campo}_nome`]: row.nome }));
    setModalBusca(null);
  };

  const handleCodigoSalvar = cb => {
    setForm(f => {
      let lista = f.codigos_barras.map(c => ({ ...c }));
      // Garante apenas um Principal
      if (cb.principal) lista = lista.map(c => ({ ...c, principal: false }));
      if (modalCodigo.indice !== null) lista[modalCodigo.indice] = cb;
      else lista.push(cb);
      return { ...f, codigos_barras: lista };
    });
    setModalCodigo({ aberto: false, indice: null, inicial: null });
  };

  const isSaving = criarMutation.isPending || atualizarMutation.isPending;

  /* Atalhos de teclado — mesmo esquema das demais telas de cadastro
     (Clientes/Produtos/Usuários): F2 Alterar, F3 Buscar, F4 Novo,
     F8 Excluir, F9 Salvar, Esc Cancelar. */
  useEffect(() => {
    const handler = (e) => {
      if (modalBusca || modalCompras || modalCodigo.aberto || modalUnidades) return;
      // Navegação por teclado do F3 (mesmo comportamento do PDV) — precisa
      // vir antes do "return" genérico de modal aberto.
      if (modalBuscaProd) {
        if (e.key === 'Escape') { fecharF3(); return; }
        if (e.key === 'Enter' && idxF3 !== null) { e.preventDefault(); confirmarF3(produtosF3[idxF3]); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setIdxF3(i => i === null ? 0 : Math.min(i + 1, produtosF3.length - 1)); return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setIdxF3(i => i === null ? 0 : Math.max(i - 1, 0)); return; }
        return;
      }
      if (e.key === 'F4' && !editando) { e.preventDefault(); handleNovo(); return; }
      if (e.key === 'F3' && !editando) { e.preventDefault(); abrirF3(); return; }
      if (e.key === 'F2' && !editando && form.id) { e.preventDefault(); handleAlterar(); return; }
      if (e.key === 'F8' && !editando && form.id) { e.preventDefault(); handleExcluir(); return; }
      if (e.key === 'F9' && editando && !isSaving) { e.preventDefault(); handleSalvar(); return; }
      if (e.key === 'Escape' && editando) { e.preventDefault(); handleCancelar(); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editando, form.id, modalBusca, modalCompras, modalBuscaProd, modalCodigo.aberto, modalUnidades, isSaving, idxF3, produtosF3, confirmarF3, fecharF3, abrirF3]); // eslint-disable-line

  /* ── Status bar text ── */
  const statusText = () => {
    if (modo === 'novo') return <span className={s.statusNovo}>INSERINDO NOVO PRODUTO</span>;
    if (modo === 'editar') return <span className={s.statusEditar}>ALTERANDO PRODUTO</span>;
    if (form.id) return `Produto ${form.codigo_interno} — ${lista.length} registro(s) | Pos: ${posicao + 1}`;
    return 'Nenhum produto carregado';
  };

  /* ── Render ── */
  return (
    <>
      <div className={s.erpWrap} data-enter-scope="on" onKeyDown={navegarComEnter}>
        {/* Barra de título */}
        <div className={s.titleBar}>
          <span>📦</span> Cadastro de Produtos
        </div>

        {/* Campos do cabeçalho */}
        <div className={s.headerRow}>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Código</span>
            <input className={s.inputErp} style={{ width: 80 }}
              value={form.codigo_interno} readOnly />
          </div>
          <div className={s.headerGroup} style={{ flex: 1 }}>
            <span className={s.headerLabel}>Descrição</span>
            {/* Somente-leitura (produto carregado via busca/navegação, fora do modo
                Alterar) usa .inputErp:read-only, que é cinza claro — nome do produto
                é o principal jeito de confirmar visualmente qual produto está aberto,
                então força cor escura + negrito aqui mesmo nesse estado. */}
            <input className={s.inputErp}
              style={{ flex: 1, ...(!editando ? { color: 'var(--text)', fontWeight: 700 } : {}) }}
              value={form.nome} readOnly={!editando}
              onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
          </div>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>UN</span>
            <select className={s.selectErp} style={{ width: 90 }}
              value={form.unidade} disabled={!editando}
              onChange={e => setForm(f => ({ ...f, unidade: e.target.value }))}>
              {/* Garante que o valor atual apareça mesmo que não esteja na lista */}
              {form.unidade && !unidades.find(u => u.codigo === form.unidade) && (
                <option value={form.unidade}>{form.unidade}</option>
              )}
              {unidades.map(u => (
                <option key={u.id} value={u.codigo}>{u.codigo} — {u.descricao}</option>
              ))}
            </select>
            <button className={s.btnLookup} title="Gerenciar unidades"
              onClick={() => setModalUnidades(true)}>
              ...
            </button>
          </div>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Tipo</span>
            <select className={s.selectErp} value={form.tipo} disabled={!editando}
              onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
              {TIPOS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          {form.tipo === 'serviço' && (
            <div className={s.headerGroup}>
              <span className={s.headerLabel}>Serviço (LC 116)</span>
              <select className={s.selectErp} style={{ minWidth: 260 }}
                value={form.servico_lc116_codigo || ''} disabled={!editando}
                onChange={e => setForm(f => ({ ...f, servico_lc116_codigo: e.target.value }))}>
                <option value="">— não vinculado —</option>
                {listaServicosLc116.map(sv => (
                  <option key={sv.codigo} value={sv.codigo}>{sv.codigo} — {sv.descricao}</option>
                ))}
              </select>
              {editando && (
                <button type="button" className={s.btnLookup}
                  title={verTodosServicos ? 'Mostrar só os sugeridos pelos CNAEs da organização' : 'Ver lista completa (sem filtro pelos CNAEs)'}
                  onClick={() => setVerTodosServicos(v => !v)}>
                  {verTodosServicos ? '☆ Sugeridos' : '☰ Ver todos'}
                </button>
              )}
            </div>
          )}
          {form.tipo === 'serviço' && (
            <div className={s.headerGroup} title="Usado pela Agenda pra somar o tempo do atendimento. Em branco = usa um padrão fixo.">
              <span className={s.headerLabel}>Duração (min)</span>
              <input className={s.inputErp} style={{ width: 80 }} type="number" min="1" step="1"
                value={form.duracao_min} disabled={!editando}
                onChange={e => setForm(f => ({ ...f, duracao_min: e.target.value }))} />
            </div>
          )}
          <div className={s.checkRow}>
            <input type="checkbox" id="chkInativo" checked={form.inativo} disabled={!editando}
              onChange={e => setForm(f => ({ ...f, inativo: e.target.checked }))} />
            <label htmlFor="chkInativo">Inativo</label>
          </div>
          <div className={s.checkRow}>
            <input type="checkbox" id="chkMarcador" checked={form.marcador} disabled={!editando}
              onChange={e => setForm(f => ({ ...f, marcador: e.target.checked }))} />
            <label htmlFor="chkMarcador">Marcador</label>
          </div>
          <div className={s.checkRow}>
            <input type="checkbox" id="chkComposto" checked={form.composto} disabled={!editando}
              onChange={e => setForm(f => ({ ...f, composto: e.target.checked }))} />
            <label htmlFor="chkComposto">Composto</label>
          </div>
        </div>

        {/* Abas */}
        <div className={s.tabBar}>
          {ABAS.filter(a => (a.id !== 'ficha' || form.composto) && (a.id !== 'curso' || form.tipo === 'serviço')).map(a => (
            <button key={a.id}
              className={`${s.tab} ${aba === a.id ? s.tabAtiva : ''}`}
              onClick={() => setAba(a.id)}>
              {a.label}
            </button>
          ))}
        </div>

        {/* Conteúdo da aba */}
        <div className={s.tabContent}>
          {aba === 'geral' ? (
            <AbaGeral
              form={form}
              setForm={setForm}
              editando={editando}
              setModalBusca={setModalBusca}
              setModalCodigo={setModalCodigo}
            />
          ) : aba === 'fiscal' ? (
            <AbaFiscal
              form={form}
              setForm={setForm}
              editando={editando}
              unidades={unidades}
            />
          ) : aba === 'precos' ? (
            <AbaPrecos produtoId={form.id || null} />
          ) : aba === 'fornecedores' ? (
            <AbaFornecedores produtoId={form.id || null} />
          ) : aba === 'foto' ? (
            <AbaFotos produtoId={form.id || null} />
          ) : aba === 'curso' ? (
            <AbaCurso produtoId={form.id || null} />
          ) : aba === 'ficha' ? (
            <AbaFichaTecnica produtoId={form.id || null} form={form} setForm={setForm} editando={editando} onEstoqueOuCustoAlterado={atualizarEstoqueECusto} />
          ) : aba === 'plataformas' ? (
            <AbaPlataformas form={form} setForm={setForm} editando={editando} />
          ) : aba === 'lotes' ? (
            <AbaLotes produtoId={form.id || null} />
          ) : (
            <div className={s.abaPlaceholder}>
              Aba "{ABAS.find(a => a.id === aba)?.label}" — em desenvolvimento
            </div>
          )}
        </div>

        {/* Barra de status */}
        <div className={s.statusBar}>{statusText()}</div>

        {/* Rodapé */}
        <div className={s.footer}>
          <div className={s.footerLeft}>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navPrimeiro} disabled={editando || posicao === 0} title="Primeiro registro">
              &lt;|
            </button>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navAnterior} disabled={editando || posicao === 0} title="Registro anterior">
              &lt;
            </button>
            <span className={s.footerPos}>{lista.length ? `${posicao + 1} / ${lista.length}` : '0 / 0'}</span>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navProximo} disabled={editando || posicao >= lista.length - 1} title="Próximo registro">
              &gt;
            </button>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navUltimo} disabled={editando || posicao >= lista.length - 1} title="Último registro">
              &gt;|
            </button>
            {filtroNav && (
              <button className={s.btnErp} onClick={limparFiltroNav} disabled={editando}
                style={{ color: '#1565c0', fontWeight: 600 }}
                title="Navegando só pelos produtos selecionados no F3 — clique para voltar a todos">
                Filtro: {filtroNav.length} produtos ✕
              </button>
            )}
            <div className={s.footerSep} />
            <button className={s.btnErp} onClick={handleClonar}
              disabled={!form.id || editando || clonarMutation.isPending}>
              Clonar
            </button>
            <button className={s.btnErp} disabled={!form.id || editando}>
              Variações
            </button>
            <button className={s.btnErp} onClick={() => setModalCompras(true)}
              disabled={!form.id || editando}>
              Últimas Compras
            </button>
          </div>
          <div className={s.footerRight}>
            <button className={s.btnErp} onClick={handleNovo} disabled={editando}>
              Novo (F4)
            </button>
            <button className={s.btnErp} onClick={abrirF3} disabled={editando}>
              Buscar (F3)
            </button>
            <button className={s.btnErp} onClick={handleAlterar} disabled={editando || !form.id}>
              Alterar (F2)
            </button>
            <button className={s.btnErp} onClick={handleCancelar} disabled={!editando}>
              Cancelar (Esc)
            </button>
            <button className={`${s.btnErp} ${s.btnErpPerigo}`}
              onClick={handleExcluir} disabled={editando || !form.id}>
              Excluir (F8)
            </button>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} data-enter-final
              onClick={handleSalvar} disabled={!editando || isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar (F9)'}
            </button>
            <button className={s.btnErp}
              onClick={() => window.history.back()}>
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* ── Modais ── */}
      {modalBusca && (
        <ModalBusca
          tipo={modalBusca}
          titulo={BUSCA_META[modalBusca]?.titulo}
          endpoint={BUSCA_META[modalBusca]?.endpoint}
          campos={BUSCA_META[modalBusca]?.campos}
          onSelect={handleSelectBusca}
          onClose={() => setModalBusca(null)}
        />
      )}

      {modalCompras && (
        <ModalUltimasCompras
          produtoId={form.id}
          nomeProduto={form.nome}
          onClose={() => setModalCompras(false)}
        />
      )}

      <ModalBuscaProdutos
        modalF3={modalBuscaProd} fecharF3={fecharF3}
        filtrosF3={filtrosF3} setFiltro={setFiltro} setFiltrosF3={setFiltrosF3}
        FILTROS_VAZIOS={FILTROS_F3_VAZIOS}
        produtosF3={produtosF3} setProdutosF3={setProdutosF3}
        buscaRapidaF3={buscaRapidaF3} setBuscaRapidaF3={setBuscaRapidaF3}
        handleBuscaRapidaF3={handleBuscaRapidaF3} buscandoF3={buscandoF3}
        buscarF3={buscarF3} idxF3={idxF3} setIdxF3={setIdxF3}
        confirmarF3={confirmarF3} tipoMovFiscal={null} buscaRapidaRef={buscaRapidaRef}
        avancado selecao confirmarSelecionados={confirmarSelecionadosF3}
      />

      {modalCodigo.aberto && (
        <ModalCodigo
          inicial={modalCodigo.inicial}
          produtoId={form.id}
          onSalvar={handleCodigoSalvar}
          onClose={() => setModalCodigo({ aberto: false, indice: null, inicial: null })}
        />
      )}

      {modalUnidades && (
        <ModalUnidades
          onSelect={codigo => {
            setForm(f => ({ ...f, unidade: codigo }));
            setModalUnidades(false);
          }}
          onClose={() => setModalUnidades(false)}
        />
      )}
    </>
  );
}
